/**
 * GrapesEmailEditor — Visual MJML email template editor
 *
 * Drag-and-drop email builder using GrapesJS + grapesjs-mjml.
 * MJML auto-compiles to Outlook/Gmail/mobile-safe table-based HTML.

 *
 * Save flow:
 *   editor.runCommand('mjml-code-to-html') → compiled HTML → onChange(html)
 *   editor.getProjectData()                → JSON          → onJsonChange(json)


 */
import { useRef, useCallback, useEffect, useMemo } from 'react';
import type { Editor, ProjectData } from 'grapesjs';
import grapesjs from 'grapesjs';
import GjsEditor from '@grapesjs/react';
import mjmlPlugin from 'grapesjs-mjml';
import type { TemplateVariable } from '@/types/email-template.types';
import { htmlToMjml } from '@/utils/html-to-mjml';
import { cleanEditorArtifacts } from '@/utils/email-editor-cleanup';
import { variableLabel } from '@/utils/react-email-converter';
import { storageService } from '@/services/storage.service';
import toast from 'react-hot-toast';

import 'grapesjs/dist/css/grapes.min.css';

// ============== TYPES ==============

interface GrapesEmailEditorProps {
  value: string;
  onChange: (html: string) => void;
  editorJson?: ProjectData | null;
  onJsonChange?: (json: ProjectData) => void;
  variables?: TemplateVariable[];
  minHeight?: string;
  error?: string;
  label: string;
  required?: boolean;
  /** Called once with the GrapesJS editor instance — lets parent trigger
   *  BlockManager.startDrag() from custom UI (e.g., external Variables panel) */
  onEditorReady?: (editor: Editor) => void;
}

// ============== MJML PLUGIN CONFIG ==============

/**
 * MJML plugin wrapper — module-level for stable reference across renders.
 *
 * grapesjs-mjml replaces default blocks/styles/devices with email-specific ones:
 *   - Blocks: mj-section, mj-column, mj-text, mj-button, mj-image, mj-divider, etc.
 *   - Style Manager: padding, background, font, border properties relevant to email
 *   - Devices: Desktop (600px) + Mobile presets

 */
const mjmlWithOptions = (editor: Editor) => {
  mjmlPlugin(editor, {
    resetBlocks: true,       
    resetStyleManager: true,  
    resetDevices: true,       
    hideSelector: true,       
    columnsPadding: '10px 0', 
  });
};

/** Stable array ref — prevents GjsEditor re-init on parent re-renders */
const PLUGINS = [mjmlWithOptions];

// ============== DEFAULT TEMPLATE ==============

/** Starting MJML skeleton for brand-new templates. */
const DEFAULT_MJML_TEMPLATE = `<mjml>
  <mj-head>
    <mj-attributes>
      <mj-all font-family="Arial, sans-serif" />
      <mj-text font-size="14px" color="#555555" line-height="1.6" />
      <mj-section padding="20px 30px" />
    </mj-attributes>
  </mj-head>
  <mj-body background-color="#f5f5f5">
    <mj-section background-color="#4CAF50" padding="30px" border-radius="8px 8px 0 0">
      <mj-column>
        <mj-text align="center" color="#ffffff" font-size="24px" font-weight="bold">
          Email Title Here
        </mj-text>
      </mj-column>
    </mj-section>
    <mj-section background-color="#ffffff" padding="30px">
      <mj-column>
        <mj-text font-size="16px" color="#333333">
          Hello {{user_name}},
        </mj-text>
        <mj-text>
          Drag blocks from the left panel to build your email. Select any element to edit its styles on the right.
        </mj-text>
      </mj-column>
    </mj-section>
    <mj-section background-color="#ffffff" padding="10px 30px 30px">
      <mj-column>
        <mj-button background-color="#4CAF50" href="{{login_url}}" font-size="16px" border-radius="25px">
          Call to Action
        </mj-button>
      </mj-column>
    </mj-section>
    <mj-section background-color="#f8f9fa" padding="20px 30px" border-radius="0 0 8px 8px">
      <mj-column>
        <mj-text align="center" font-size="12px" color="#999999">
          &copy; {{current_year}} {{company_name}}. All rights reserved.
        </mj-text>
      </mj-column>
    </mj-section>
  </mj-body>
</mjml>`;

// ============== DROP-ZONE UX ==============

/**
 * Inject drag-aware CSS into the canvas iframe + toggle a `gjs-is-dragging`
 * class on the canvas root during drag.  Two-state design:
 *
 *   STATE 1 — idle (not dragging):
 *     • Empty containers show a subtle "Drop content here" dashed area
 *     • Filled containers render normally, no visual noise
 *
 *   STATE 2 — dragging (user holds a block from the library):
 *     • ALL valid drop targets (empty + non-empty containers) get a
 *       highlighted outline + hint label so the user can aim
 *     • Drop zones glow stronger to feel "targetable"
 *     • Filled containers show insertion-point hints between children

 */
const injectCanvasDropZoneCSS = (editor: Editor) => {
  try {
    const frame = editor.Canvas.getFrameEl() as HTMLIFrameElement | null;
    const doc = frame?.contentDocument;
    if (!doc) return;
    if (doc.getElementById('gjs-dropzone-helper')) return;

    const style = doc.createElement('style');
    style.id = 'gjs-dropzone-helper';
    style.textContent = `
      /* ─────── STATE 1: idle — subtle hint on empty containers ─────── */
      [data-gjs-type="mj-body"]:empty,
      [data-gjs-type="mj-section"]:empty,
      [data-gjs-type="mj-column"]:empty,
      [data-gjs-type="mj-wrapper"]:empty,
      [data-gjs-type="mj-hero"]:empty,
      [data-gjs-type="mj-group"]:empty {
        min-height: 70px !important;
        background-color: rgba(99, 102, 241, 0.04) !important;
        border: 2px dashed rgba(99, 102, 241, 0.3) !important;
        box-sizing: border-box !important;
        position: relative !important;
        transition: all 150ms ease !important;
      }
      [data-gjs-type="mj-body"]:empty::before,
      [data-gjs-type="mj-section"]:empty::before,
      [data-gjs-type="mj-column"]:empty::before,
      [data-gjs-type="mj-wrapper"]:empty::before,
      [data-gjs-type="mj-hero"]:empty::before,
      [data-gjs-type="mj-group"]:empty::before {
        content: "Drop content here";
        position: absolute;
        top: 50%;
        left: 50%;
        transform: translate(-50%, -50%);
        color: rgba(99, 102, 241, 0.65);
        font: 600 12px Arial, sans-serif;
        letter-spacing: 0.3px;
        pointer-events: none;
        white-space: nowrap;
      }

      /* ─────── STATE 2: dragging — HIGHLIGHT ALL DROP TARGETS ─────── */
      body.gjs-is-dragging [data-gjs-type="mj-body"],
      body.gjs-is-dragging [data-gjs-type="mj-section"],
      body.gjs-is-dragging [data-gjs-type="mj-column"],
      body.gjs-is-dragging [data-gjs-type="mj-wrapper"],
      body.gjs-is-dragging [data-gjs-type="mj-hero"],
      body.gjs-is-dragging [data-gjs-type="mj-group"] {
        outline: 2px solid rgba(99, 102, 241, 0.45) !important;
        outline-offset: -2px !important;
        background-color: rgba(99, 102, 241, 0.03) !important;
        transition: all 120ms ease !important;
      }
      body.gjs-is-dragging [data-gjs-type="mj-body"]:empty,
      body.gjs-is-dragging [data-gjs-type="mj-section"]:empty,
      body.gjs-is-dragging [data-gjs-type="mj-column"]:empty,
      body.gjs-is-dragging [data-gjs-type="mj-wrapper"]:empty,
      body.gjs-is-dragging [data-gjs-type="mj-hero"]:empty,
      body.gjs-is-dragging [data-gjs-type="mj-group"]:empty {
        min-height: 80px !important;
        background-color: rgba(99, 102, 241, 0.12) !important;
        border: 2px dashed rgba(99, 102, 241, 0.75) !important;
        outline: none !important;
        box-shadow: 0 0 0 4px rgba(99, 102, 241, 0.1) !important;
      }
      body.gjs-is-dragging [data-gjs-type="mj-body"]:empty::before,
      body.gjs-is-dragging [data-gjs-type="mj-section"]:empty::before,
      body.gjs-is-dragging [data-gjs-type="mj-column"]:empty::before,
      body.gjs-is-dragging [data-gjs-type="mj-wrapper"]:empty::before,
      body.gjs-is-dragging [data-gjs-type="mj-hero"]:empty::before,
      body.gjs-is-dragging [data-gjs-type="mj-group"]:empty::before {
        content: "✦  Drop here  ✦";
        color: rgb(67, 71, 188);
        font-size: 13px;
        font-weight: 700;
        letter-spacing: 0.5px;
      }

      /* Insertion point indicator — GrapesJS shows a green placeholder line.
         Make it thicker and more visible. */
      .gjs-placeholder {
        background-color: rgb(99, 102, 241) !important;
        opacity: 1 !important;
        height: 3px !important;
      }
      .gjs-placeholder.horizontal {
        height: 3px !important;
      }
      .gjs-placeholder.vertical {
        width: 3px !important;
      }
      .gjs-placeholder-int {
        background-color: rgb(99, 102, 241) !important;
        border: 2px solid rgb(99, 102, 241) !important;
        box-shadow: 0 0 8px rgba(99, 102, 241, 0.6) !important;
      }

      /* Dim sibling drop zones that AREN'T being hovered so the target
         drop zone visually pops.  GrapesJS adds the .gjs-hovered class
         to the current hover target during a drag. */
      body.gjs-is-dragging [data-gjs-type="mj-section"]:not(.gjs-hovered):not(:hover) {
        opacity: 0.85;
      }
      body.gjs-is-dragging .gjs-hovered {
        outline: 2px solid rgb(99, 102, 241) !important;
        outline-offset: -2px !important;
        background-color: rgba(99, 102, 241, 0.08) !important;
      }
    `;
    doc.head.appendChild(style);

    // Toggle `.gjs-is-dragging` on the canvas body during drag operations.
    // GrapesJS fires block:drag when a block from the library is being dragged,
    // and component:drag when an existing component is being moved.
    const body = doc.body;
    const setDragging = (on: boolean) => {
      if (!body) return;
      body.classList.toggle('gjs-is-dragging', on);
    };
    editor.on('block:drag:start', () => setDragging(true));
    editor.on('block:drag:stop', () => setDragging(false));
    editor.on('component:drag:start', () => setDragging(true));
    editor.on('component:drag:end', () => setDragging(false));
    editor.on('sorter:drag:start', () => setDragging(true));
    editor.on('sorter:drag:end', () => setDragging(false));
  } catch (err) {
    console.warn('[GrapesEmailEditor] Drop-zone CSS injection failed:', err);
  }
};

// ============== CLICK AUTO-DRILL ==============

/**
 * The function body is replaced with a no-op so call sites keep working but
 * no auto-drill runs.  Keep the signature intact for future re-enable.
 */

const enableTextAutoDrill = (_editor: Editor) => {
  // Intentionally empty — see doc comment above for context.
};

// ============== COPY BUTTON ON A COLUMN ==============

// Copying a Column put the copy beside it (a 2-column row). The editor can't reopen
// 2-column rows (the 2nd column is dropped), so copy the whole row below instead.
// Every other block copies exactly as before (GrapesJS's own copy + paste).
const cloneColumnAsSection = (editor: Editor) => {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const cmds = (editor as any).Commands;
  if (!cmds?.add) return;
  cmds.add('tlb-clone', {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    run(ed: any) {
      const sel = ed.getSelected();
      const parent = sel?.parent?.();
      if (sel?.get?.('type') === 'mj-column' && parent?.get?.('type') === 'mj-section') {
        ed.select(parent);
      }
      ed.runCommand('core:copy');
      ed.runCommand('core:paste', { action: 'clone-component' });
    },
  });
};

// ============== LIST NUMBER COLOUR ==============

// A list number takes the <li> colour, not the text's. When the user colours a whole item,
// the <li> gets that colour too (marked); it is removed again on undo or mixed colours.
const LI_COLOR_MARK = 'data-sz-li-color';

// Colour picked for this text: the nearest coloured <span>/<font> inside the item, else null.
const pickedTextColor = (text: Node, li: Element): string | null => {
  for (let el = text.parentElement; el && el !== li; el = el.parentElement) {
    const tag = el.tagName.toLowerCase();
    if (tag === 'span' && el.style.color) return colorToHex(el.style.color) || el.style.color;
    if (tag === 'font' && el.getAttribute('color')) return colorToHex(el.getAttribute('color') || '');
  }
  return null;
};

// apply: true right after the user coloured text (button or pen); false = only keep marks in sync.
const syncListMarkerColor = (doc: Document, apply: boolean) => {
  try {
    const sel = doc.getSelection();
    if (!sel || !sel.rangeCount) return;
    const r = sel.getRangeAt(0);
    const elementOf = (n: Node) => (n.nodeType === 1 ? (n as Element) : n.parentElement);
    const startEl = elementOf(r.startContainer);
    if (!startEl?.closest('[contenteditable="true"]')) return;

    const items = new Set<HTMLLIElement>();
    for (const el of [startEl, elementOf(r.endContainer)]) {
      const li = el?.closest('li') as HTMLLIElement | null;
      if (li) items.add(li);
    }
    if (!r.collapsed) {
      const common = elementOf(r.commonAncestorContainer);
      common?.querySelectorAll('li').forEach((li) => { if (r.intersectsNode(li)) items.add(li as HTMLLIElement); });
    }

    items.forEach((li) => {
      let color: string | null | undefined; // undefined = no text seen yet
      let one_color = true;
      const walker = doc.createTreeWalker(li, NodeFilter.SHOW_TEXT);
      for (let n = walker.nextNode(); n; n = walker.nextNode()) {
        const parent = n.parentElement;
        if (!parent || !n.textContent?.trim() || parent.closest('li') !== li) continue; // nested items decide their own
        const c = pickedTextColor(n, li);
        if (color === undefined) color = c;
        else if (c !== color) { one_color = false; break; }
      }
      const marked = li.hasAttribute(LI_COLOR_MARK);
      if (one_color && color) {
        if (apply || marked) {
          if (colorToHex(li.style.color) !== color) li.style.color = color;
          li.setAttribute(LI_COLOR_MARK, '');
        }
      } else if (marked) {
        li.style.removeProperty('color');
        li.removeAttribute(LI_COLOR_MARK);
        if (!li.getAttribute('style')) li.removeAttribute('style');
      }
    });
  } catch { /* never block typing */ }
};

// ============== TEXT COLOUR SWATCH ==============

// <input type="color"> fires 'input' only when its value changes. It kept the last picked
// colour, so picking that same colour on black text did nothing ("works the second time").
// Keep the swatch equal to the colour at the caret: any other pick is then a real change.
const colorToHex = (value: string): string | null => {
  const v = (value || '').trim().toLowerCase();
  if (/^#[0-9a-f]{6}$/.test(v)) return v;
  if (/^#[0-9a-f]{3}$/.test(v)) return '#' + v.slice(1).split('').map((c) => c + c).join('');
  const m = v.match(/^rgba?\(\s*(\d{1,3})\s*,\s*(\d{1,3})\s*,\s*(\d{1,3})/);
  if (!m) return null;
  return '#' + [m[1], m[2], m[3]].map((n) => Math.min(255, Number(n)).toString(16).padStart(2, '0')).join('');
};

// While a pen colour is active the swatch shows it; otherwise the colour at the caret.
const syncTextColorSwatch = (editor: Editor, doc: Document) => {
  try {
    const input = document.querySelector('.gjs-rte-color') as HTMLInputElement | null;
    if (!input) return;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const pen_active = !!(editor as any).__penColor && editor.getSelected()?.cid === (editor as any).__penBlockCid;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const pen = pen_active ? (editor as any).__penColor as string : null;
    const hex = colorToHex(pen || String(doc.queryCommandValue('foreColor') || ''));
    if (hex && input.value.toLowerCase() !== hex) input.value = hex;
  } catch { /* never block typing */ }
};

const PEN_LINE_TAGS = 'p,div,li,h1,h2,h3,h4,h5,h6,td,th,blockquote,pre';

// Caret spot inside the block being edited: "characters before it @ line index" (null if unknown).
// The line part tells "end of line 1" from "start of line 2", which have the same character count.
const penCaretSpot = (editor: Editor, doc: Document): string | null => {
  try {
    const sel = doc.getSelection();
    const el = (editor.getSelected() as any)?.view?.el as HTMLElement | undefined; // eslint-disable-line @typescript-eslint/no-explicit-any
    if (!sel || !sel.rangeCount || !el) return null;
    const r = sel.getRangeAt(0);
    if (!el.contains(r.startContainer)) return null;
    const before = doc.createRange();
    before.setStart(el, 0);
    before.setEnd(r.startContainer, r.startOffset);
    const node = r.startContainer;
    const line = (node.nodeType === 1 ? (node as Element) : node.parentElement)?.closest(PEN_LINE_TAGS);
    const line_index = line && el.contains(line) && line !== el
      ? Array.prototype.indexOf.call(el.querySelectorAll(PEN_LINE_TAGS), line)
      : -1;
    return `${before.toString().length}@${line_index}`;
  } catch { return null; }
};

const clearPen = (editor: Editor) => {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const ed = editor as any;
  ed.__penColor = null;
  ed.__penBlockCid = null;
  ed.__penSpot = null;
};

// Pen colour: a colour picked with nothing selected. The browser keeps it only until the
// next click, so the editor keeps it and applies it just before each typed character.
// It belongs to the caret spot: typing moves it along; clicking/arrowing elsewhere ends it.
const keepPenColor = (editor: Editor) => {
  const attach = () => {
    const doc = editor.Canvas.getDocument() as (Document & { __szPenColor?: boolean }) | null;
    if (!doc || doc.__szPenColor) return;
    doc.__szPenColor = true;
    doc.addEventListener('beforeinput', (e: Event) => {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const pen = (editor as any).__penColor as string | null;
      if (!pen || (e as InputEvent).inputType !== 'insertText') return;
      if (!(e.target as HTMLElement | null)?.isContentEditable) return;
      // Only inside the text block where the pen colour was picked.
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      if (editor.getSelected()?.cid !== (editor as any).__penBlockCid) return;
      try {
        if (colorToHex(String(doc.queryCommandValue('foreColor') || '')) === colorToHex(pen)) return;
        doc.execCommand('styleWithCSS', false, 'true');
        doc.execCommand('foreColor', false, pen);
      } catch { /* never block typing */ }
    }, true);
    // Typing (incl. Enter) moves the pen spot along with the caret.
    doc.addEventListener('input', () => {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      if ((editor as any).__penColor) (editor as any).__penSpot = penCaretSpot(editor, doc);
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      syncListMarkerColor(doc, !!(editor as any).__penColor);
    }, true);
    // Caret moved somewhere else → pen ends; typing follows the colour already there.
    const endPenIfMoved = () => {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const ed = editor as any;
      if (!ed.__penColor) return;
      const spot = penCaretSpot(editor, doc);
      if (spot === null || spot === ed.__penSpot) return;
      clearPen(editor);
      syncTextColorSwatch(editor, doc);
    };
    doc.addEventListener('mouseup', endPenIfMoved, true);
    doc.addEventListener('keyup', (e: KeyboardEvent) => {
      if (/^(Arrow|Home$|End$|Page)/.test(e.key)) endPenIfMoved();
    }, true);
  };
  attach();
  editor.on('canvas:frame:load', attach);
};

// ============== STYLABLE EXPANSION (whole-tree, one-shot) ==============

/**
 * Widen the `stylable` list on every component in the canvas so the
 * Decorations sector has matching properties to render.
 *
 * Why: grapesjs-mjml registers narrow `stylable` lists on MJML components
 * (e.g. mj-text only has font/color props, no bg or border) — and raw HTML
 * table elements that come from htmlToMjml conversion arrive with an even
 * narrower list.  The Style Manager's Decorations sector ends up with zero
 * matching props for those components → GrapesJS auto-hides the empty sector.
 *
 * Why NOT on component:selected: running this on every click is high-frequency
 * and brittle — each selection triggers model mutation, which (even with
 * silent: true) is a state drift the compile path has to tolerate.  Any
 * `mjml-code-to-html` run that happened to interleave with a rapid-click
 * sequence risked reading the model mid-mutation.
 *
 * Strategy: run ONCE after the template loads (covers existing tree) + once
 * per new component:add event (covers blocks the admin drops later).  Both
 * calls use `silent: true` — no component:update event, no debouncedExtract.
 */
const STYLABLE_BG_HOSTS = [
  'mj-section',
  'mj-column',
  'mj-button',
  'mj-wrapper',
  'mj-hero',
  'mj-body',
];
const RAW_TABLE_TAGS = ['table', 'tr', 'td', 'th', 'tbody', 'thead'];

const expandStylableOnComponent = (comp: unknown): void => {
  try {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const c = comp as any;
    if (!c || typeof c.get !== 'function') return;
    const type = (c.get('type') || '').toString();
    const tagName = (c.get('tagName') || '').toString().toLowerCase();
    const current = c.get('stylable');
    // stylable === true (allow all) or undefined → nothing to widen
    if (!Array.isArray(current)) return;

    // MJML components: grapesjs-mjml gives each one either `background-color`
    // (mj-section/mj-column/mj-button/mj-wrapper/mj-hero/mj-body) OR
    // `container-background-color` (mj-text/mj-image/mj-spacer/mj-divider/
    //  mj-navbar-link/etc.) — NEVER both. We mirror that rule here so the
    // sidebar never ends up with two "Background color" fields for one
    // component.
    //
    // Typography expansion: the plugin's default stylable for mj-section/
    // mj-column has only `text-align` on the Typography side — font-size/
    // weight/family are reserved for mj-text. But admins get confused when
    // clicking a section shows an almost-empty Typography sector next to a
    // fully populated one for mj-text. Widen the section/column stylable
    // with the font props so the Typography panel is consistent. Note: MJML
    // compile emits these as inline CSS on the wrapper <td>, which email
    // clients that support CSS inheritance (Gmail, Apple Mail) will apply
    // to descendant text. Outlook strips inheritance — that's an MJML limit,
    // not ours.
    const TYPO_PROPS = [
      'font-family',
      'font-size',
      'font-weight',
      'font-style',
      'color',
      'line-height',
      'letter-spacing',
      'text-align',
      'text-decoration',
      'text-transform',
    ];
    let needed: string[] | null = null;
    // Button-units (a styled <a>/<button>/<table>-wrapped button promoted by
    // markMjTextDescendants) need both Decorations AND Typography sectors
    // populated — admins want to recolor the button bg AND change the button
    // text color/font/etc. from the same sidebar selection.
    if (c.get('__buttonUnit')) {
      needed = [
        'background-color',
        'border', 'border-radius', 'border-color',
        'padding',
        // Typography props — `color` is what gives the user a "Text color"
        // control inside the Typography sector. Without this, only bg was
        // pickable and the button's white text stayed white.
        ...TYPO_PROPS,
      ];
    } else if (type.startsWith('mj-')) {
      // Unified bg prop for all MJML components: the sidebar only shows a
      // `background-color` control (see cleanupStyleManager's Decorations
      // cleanup). A separate sync listener maps CSS `background-color` to
      // the MJML `container-background-color` attribute for components that
      // need the attribute form in compiled email (mj-text/button/image).
      needed = ['background-color', 'border', 'border-radius', 'border-color'];
      // Containers also get font props so Typography panel is not empty.
      if (STYLABLE_BG_HOSTS.includes(type)) {
        needed = [...needed, ...TYPO_PROPS];
      }
    } else if (RAW_TABLE_TAGS.includes(tagName)) {
      needed = [
        'background-color',
        'border',
        'border-radius',
        'border-color',
        'padding',
        ...TYPO_PROPS,
      ];
    }
    if (!needed) return;

    const missing = needed.filter((p) => !current.includes(p));
    if (missing.length === 0) return;
    // silent: true — no component:update event, so no debouncedExtract bounce,
    // no clash with an in-flight mjml-code-to-html command.
    c.set('stylable', [...current, ...missing], { silent: true });
  } catch {
    /* per-component failure must not break the tree walk */
  }
};

const expandStylableAcrossTree = (editor: Editor): void => {
  try {
    const wrapper = editor.DomComponents?.getWrapper?.();
    if (!wrapper) return;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const walk = (comp: any) => {
      expandStylableOnComponent(comp);
      comp.components?.()?.each?.(walk);
    };
    walk(wrapper);
  } catch (err) {
    console.warn('[GrapesEmailEditor] expandStylableAcrossTree failed:', err);
  }
};

// ============== STYLE MANAGER CLEANUP ==============

/**
 * Clean up the grapesjs-mjml plugin's Style Manager after plugin init.
  */
const cleanupStyleManager = (editor: Editor) => {
  try {
    const sm = editor.StyleManager;
    const sectors = sm.getSectors({ array: true });

    for (const sector of sectors) {
      const sectorName = sector.getName();
      const propsCollection = (sector as any).properties;

      // Decorations sector:
      // Remove container-background-color ONLY when a regular background-color
      // is also present (avoid 2 confusing bg color fields).  Force the sector
      // visible afterwards so it never disappears for mj-text/mj-section/etc.
      if (sectorName === 'Decorations' && propsCollection) {
        // Reading grapesjs-mjml/dist/index.js directly: the Decorations
        // sector defines `container-background-color` as a custom property
      
        // Fix: unify under ONE well-typed `background-color` property at the
        // top. Remove both the auto-built `background-color` and the plugin's
        // `container-background-color` from the sector. Below, a `style:
        // property:update` listener syncs CSS `background-color` → the MJML
        // Restored to the c5fb53e working state (verified working yesterday).
        // We replace BOTH the plugin's auto-built `background-color` and its
        // custom `container-background-color` with ONE explicitly-typed color
        // property inserted at position 0 of the Decorations sector.
        const allDecProps = sector.getProperties();
        const existingBg = allDecProps.find(
          (p) => p.get('property') === 'background-color'
        );
        const existingContainerBg = allDecProps.find(
          (p) => p.get('property') === 'container-background-color'
        );
        if (existingBg) propsCollection.remove(existingBg);
        if (existingContainerBg) propsCollection.remove(existingContainerBg);
        propsCollection.add(
          {
            type: 'color',
            name: 'Background color',
            property: 'background-color',
            defaults: '',
          },
          { at: 0 },
        );
        // Always keep the sector visible — some MJML components leave it empty
        // by default and GrapesJS auto-hides empty sectors.
        sector.set('visible', true);
        sector.set('open', true);
      }

      if (sectorName === 'Dimension' && propsCollection) {
        const removeCss = ['width', 'height', 'max-width', 'min-height', 'margin'];
        const allDimProps = sector.getProperties();
        allDimProps.forEach((prop) => {
          const css = prop.get('property') || prop.getId();
          if (removeCss.includes(css)) propsCollection.remove(prop);
        });
      }

      // Remove duplicate `align` from Typography — the MJML plugin registers
      
      if (sectorName === 'Typography' && propsCollection) {
        const allTypoProps = sector.getProperties();
        const alignProp = allTypoProps.find(
          (p) => {
            const propName = p.get('property') || p.getId();
            // Remove `align` but keep `text-align`
            return propName === 'align';
          }
        );
        if (alignProp) propsCollection.remove(alignProp);
      }
    }

    // Per-selection visibility pin for Decorations sector.
    // GrapesJS re-filters sectors on every component:selected — a one-time
    // set('visible', true) during init doesn't survive. We re-apply the flag
    // synchronously on each selection so mj-text / mj-button / mj-image
    // (which tend to have few Decoration props) still show the sector header.
    // Safe: only touches sector UI flag — no component mutation, no setTimeout,
    // no editor.select() — cannot trigger MJML rerender or extract races.
    const pinDecorationsVisible = () => {
      try {
        const allSectors = sm.getSectors({ array: true });
        for (const s of allSectors) {
          if (s.getName() === 'Decorations') {
            s.set('visible', true);
          }
        }
      } catch (e) {
        console.warn('[GrapesEmailEditor] pinDecorationsVisible failed:', e);
      }
    };
    editor.on('component:selected', pinDecorationsVisible);
    editor.on('styleManager:update', pinDecorationsVisible);

    // Root cause: grapesjs-mjml only registers Decorations props against its
    // own MJML components; raw HTML tags like <table>/<td>/<tr> come from
    // GrapesJS core and carry a shorter `stylable` list, so the Decoration
  } catch (err) {
    console.warn('[GrapesEmailEditor] Style Manager cleanup failed:', err);
  }
};

/**
 * Remove mobile/tablet device preview toggles — admins only need Desktop.
 
 */
const keepOnlyDesktopDevice = (editor: Editor) => {
  try {
    // Step 1: prune DeviceManager
    const dm = editor.Devices;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const devices = dm.getAll() as unknown as any[];
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const desktopId = devices.find((d: any) => {
      const name = (d.get('name') || '').toLowerCase();
      return name.includes('desktop');
    })?.id as string | undefined;
    devices
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      .filter((d: any) => d.id !== desktopId)
      .forEach((d: any) => dm.remove(d));
    if (desktopId) dm.select(desktopId);

    //  prune toolbar buttons in the devices panel.
    // The MJML plugin registers buttons like `set-device-desktop`,
    const panels = editor.Panels;
    const devicesPanel = panels.getPanel('devices-c');
    if (devicesPanel) {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const buttons = devicesPanel.get('buttons') as any;
      if (buttons && typeof buttons.forEach === 'function') {
      
        const toRemove: any[] = [];
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        buttons.forEach((btn: any) => {
          const id = (btn.get('id') || '').toLowerCase();
          const command = (btn.get('command') || '').toLowerCase();
          // Keep only if explicitly desktop; remove all others.
          if (!id.includes('desktop') && !command.includes('desktop')) {
            toRemove.push(btn);
          }
        });
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        toRemove.forEach((btn: any) => buttons.remove(btn));
      }
    }
  } catch (err) {
    console.warn('[GrapesEmailEditor] Could not restrict devices to Desktop:', err);
  }
};

/**
 * Remove unnecessary toolbar buttons from the options panel.
 * Keeps: sw-visibility (borders toggle), fullscreen, undo, redo.
 * Removes: preview (eye), export-template (code view), import (download).
 */
const cleanupToolbar = (editor: Editor) => {
  try {
    const panels = editor.Panels;
    const optionsPanel = panels.getPanel('options');
    if (optionsPanel) {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const buttons = optionsPanel.get('buttons') as any;
      if (buttons && typeof buttons.forEach === 'function') {
        const keepIds = ['sw-visibility', 'fullscreen', 'undo', 'redo'];
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const toRemove: any[] = [];
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        buttons.forEach((btn: any) => {
          const id = (btn.get('id') || '').toLowerCase();
          if (!keepIds.includes(id)) toRemove.push(btn);
        });
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        toRemove.forEach((btn: any) => buttons.remove(btn));
      }
    }
  } catch (err) {
    console.warn('[GrapesEmailEditor] Toolbar cleanup failed:', err);
  }
};

/**
 * Override GrapesJS's default `fullscreen` command.
 *
 * Default behaviour calls `element.requestFullscreen()` (the browser's native
 * Fullscreen API).  When the editor is in native fullscreen mode, only the
 *
 * Replace it with a CSS-class-based fullscreen: position the editor's wrapper
 * fixed to the viewport and lift its z-index.  The body is no longer hidden,
 * so popups continue to render and remain interactive.
 */
const FULLSCREEN_CLASS = 'gjs-email-editor-fullscreen';
const overrideFullscreenForPopups = (editor: Editor) => {
  try {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const Commands = (editor as any).Commands;
    if (!Commands || typeof Commands.add !== 'function') return;

    Commands.add('fullscreen', {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      run(ed: any) {
        const wrapper = ed?.getContainer?.()?.closest?.('.gjs-editor') ||
          ed?.getContainer?.();
        if (wrapper) wrapper.classList.add(FULLSCREEN_CLASS);
        document.body.classList.add(`${FULLSCREEN_CLASS}-active`);
        // Notify GrapesJS so canvas dimensions recompute.
        try { ed?.trigger?.('change:canvasOffset'); } catch (_) { /* ignore */ }
      },
      stop() {
        document
          .querySelectorAll(`.${FULLSCREEN_CLASS}`)
          .forEach((el) => el.classList.remove(FULLSCREEN_CLASS));
        document.body.classList.remove(`${FULLSCREEN_CLASS}-active`);
        try { editor?.trigger?.('change:canvasOffset'); } catch (_) { /* ignore */ }
      },
    });
  } catch (err) {
    console.warn('[GrapesEmailEditor] fullscreen override failed:', err);
  }
};

/**
 * Remove blocks that admins don't need.
 */
const cleanupBlocks = (editor: Editor) => {
  try {
    const bm = editor.BlockManager;
    const removeBlocks = ['mj-hero', 'mj-navbar-link', 'mj-social-element'];
    removeBlocks.forEach((id) => {
      if (bm.get(id)) bm.remove(id);
    });
  } catch (err) {
    console.warn('[GrapesEmailEditor] Block cleanup failed:', err);
  }
};

// ============== MJ-TEXT DESCENDANT GATING ==============


const isTraitEditableDescendant = (comp: any): boolean => {
  const type = (comp.get('type') || '').toLowerCase();
  const tagName = (comp.get('tagName') || '').toLowerCase();
  return (
    type === 'link' || tagName === 'a' ||       // <a href> — buttons/links
    type === 'image' || tagName === 'img'        // <img src> — inline images
  );
};

// ── Button-unit detection ────────────────────────────────────────────────────
// A "button-unit" is the outermost wrapper of a button rendered inline inside
// an mj-text (legacy HTML-preserved buttons via htmlToMjml).  Three shapes:
//   1. <table>…<a style="display:inline-block|background:…">…</a></table>
//   2. <a style="display:inline-block|background:…">…</a>   (bare styled anchor)
//   3. <button>…</button>
//
// We promote the outermost wrapper to a selectable "Button" unit and silence
// its descendants — so clicking anywhere on the button selects the unit, and
// the user can recolor the button independently from the surrounding mj-text.
const BUTTON_STYLE_RE = /display\s*:\s*(?:inline-)?block|background(?:-color)?\s*:/i;

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const isStyledAnchorComp = (comp: any): boolean => {
  if (!comp?.get) return false;
  if ((comp.get('tagName') || '').toString().toLowerCase() !== 'a') return false;

  // ─── Path 1 — parsed style model (most reliable in GrapesJS) ────────────
  // When GrapesJS parses HTML like <a style="display:inline-block;background:…">,
  // it lifts the inline declarations OUT of the HTML `style` attribute and
  // into the component's `style` model object.  After parsing, getAttributes()
  // typically returns no `style` key and the DOM's inline style attribute may
  // also be empty until the view fully renders.  Reading getStyle() directly
  // is the canonical way to see what styles the component actually holds.
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const styleObj = (comp.getStyle?.() || {}) as Record<string, string>;
  const display = (styleObj['display'] || '').toString().toLowerCase();
  if (display === 'inline-block' || display === 'block') return true;
  if (styleObj['background'] || styleObj['background-color']) return true;

  // ─── Path 2 — HTML attributes (some loaders keep `style` here) ──────────
  const attrs = comp.getAttributes?.() || {};
  const s = (attrs.style || '') as string;
  if (BUTTON_STYLE_RE.test(s)) return true;

  // ─── Path 3 — live DOM style attribute fallback ─────────────────────────
  // Last-resort check for setups where styles were set via setStyle and the
  // view re-rendered them onto the element but neither the model attr nor
  // getAttributes() reflects the latest state.
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const el = (comp as any).view?.el as HTMLElement | undefined;
  const live = el?.getAttribute?.('style') || '';
  return BUTTON_STYLE_RE.test(live);
};

/**
 * Strict <table> detector — qualifies as a button wrapper ONLY when the
 * table is a single-cell bulletproof-button container.  MJML's button compile
 * output is exactly:
 *     <table><tbody><tr><td><a style="...">…</a></td></tr></tbody></table>
 *
 * A multi-cell content table (e.g. a course-listing table whose Action column
 * holds a styled "Start" link) used to qualify here under the old
 * "any styled anchor anywhere" check and got mis-promoted to a Button — so
 * picking a bg-color recolored the entire content table.  Counting <td>/<th>
 * lets us tell the two apart.
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const isSingleButtonCellTable = (tableComp: any): boolean => {
  if (!tableComp?.components) return false;
  let tdCount = 0;
  let cellHasButton = false;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const walk = (c: any) => {
    if (!c?.get || tdCount > 1) return;
    const tag = (c.get('tagName') || '').toString().toLowerCase();
    if (tag === 'td' || tag === 'th') {
      tdCount += 1;
      if (tdCount > 1) return;
      // Check this cell for a styled <a> at any depth.
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const inner = (c2: any) => {
        if (cellHasButton) return;
        if (isStyledAnchorComp(c2)) { cellHasButton = true; return; }
        c2.components?.()?.each?.(inner);
      };
      c.components?.()?.each?.(inner);
    } else {
      c.components?.()?.each?.(walk);
    }
  };
  tableComp.components().each(walk);
  return tdCount === 1 && cellHasButton;
};

/**
 * Returns true if `comp` is itself the OUTERMOST wrapper of a button subtree.
 * If `comp` is something inside a button (e.g. the inner <td> of a button's
 * <table>), this returns false — only the outer wrapper qualifies, so each
 * button surfaces as exactly one selectable unit.
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const isButtonUnitWrapper = (comp: any): boolean => {
  if (!comp?.get) return false;
  const tag = (comp.get('tagName') || '').toString().toLowerCase();
  if (tag === 'button') return true;
  if (tag === 'a') return isStyledAnchorComp(comp);
  if (tag === 'table') return isSingleButtonCellTable(comp);
  return false;
};

/**
 * Recursively silence a component AND every descendant — stronger than the
 * mj-text walk which lets <a>/<img> through.  Used to silence everything
 * inside a button-unit so clicks always land on the button-unit itself.
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const silenceSubtree = (comp: any): void => {
  if (!comp?.set) return;
  comp.set(
    {
      selectable: false,
      hoverable: false,
      highlightable: false,
      layerable: false,
      editable: false,
    },
    { silent: true },
  );
  disableInnerDrag(comp);
  comp.components?.()?.each?.(silenceSubtree);
};

// Pieces inside a text block must not be draggable: a mouse-drag to select text
// would start GrapesJS "move component" (green bar) and shift the layout on release.
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const disableInnerDrag = (comp: any): void => {
  comp.set({ draggable: false }, { silent: true });
  comp.view?.el?.removeAttribute?.('draggable');
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const markMjTextDescendants = (editor: Editor) => {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const walk = (comp: any, insideMjText: boolean) => {
    if (insideMjText) {
      // ── Button-unit promotion ────────────────────────────────────────────
      // If this is a button wrapper (a <table> with a styled <a>, a bare
      // styled <a>, or a <button>), keep it SELECTABLE so the user can click
      // it directly and recolor it from the Decorations sector.  Then silence
      // every descendant so clicks inside the button always bubble up to the
      // unit.  Don't recurse — the subtree is already fully silenced.
      if (isButtonUnitWrapper(comp)) {
        comp.set(
          {
            selectable: true,
            hoverable: true,
            highlightable: true,
            layerable: true,
            editable: false,
            name: 'Button',
            // Custom marker read by the safety net, the stylable expander,
            // and the dedicated bg-color handler below.
            __buttonUnit: true,
          },
          { silent: true },
        );
        comp.components?.()?.each?.(silenceSubtree);
        return;
      }
      if (!isTraitEditableDescendant(comp)) {
        // silent: true — prevents this structural flag update from firing
        // component:update, which would bounce back into debouncedExtract and
        // create an infinite compile loop (this is called FROM extractAndNotify).
        comp.set(
          {
            selectable: false,
            hoverable: false,
            highlightable: false,
            layerable: false,
            editable: false,
          },
          { silent: true },
        );
        disableInnerDrag(comp);
      }
    }
    const isThisMjText = comp.get('type') === 'mj-text';
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    comp.components().each((child: any) =>
      walk(child, insideMjText || isThisMjText),
    );
  };
  const wrapper = editor.DomComponents.getWrapper();
  if (wrapper) walk(wrapper, false);
};

// ============== VARIABLE BLOCKS ==============

/** Format variable name for display: notes_section → Notes Section */
const formatVarName = (name: string): string => variableLabel(name);

/**
 * Register variable blocks under the "Variables" category in the block manager.
 * Each block, when dragged into the canvas, creates an <mj-text> with just the {{var}}.
 * Re-registers on every call — removes previous var blocks then adds fresh ones,
 * so changing template_type refreshes the Variables category.
 *
 * @see https://grapesjs.com/docs/api/block_manager.html#add
 */
const registerVariableBlocks = (
  editor: Editor,
  variables: TemplateVariable[]
) => {
  editor.BlockManager.getAll()
    .filter((b: { getId(): string }) => b.getId().startsWith('var-'))
    .forEach((b: { getId(): string }) => editor.BlockManager.remove(b.getId()));

  variables.forEach((v) => {
    editor.BlockManager.add(`var-${v.name}`, {
      label: v.name,
      category: 'Variables',
      // MJML string content — same format the grapesjs-mjml plugin uses for its
      // own blocks (e.g., '<mj-text>Insert text here</mj-text>').  Object notation
      // { type: 'mj-text', ... } silently fails because the MJML plugin creates
  
      content: `<mj-text padding="10px 25px">{{${v.name}}}</mj-text>`,
      media: `
        <div style="padding:8px;display:flex;align-items:center;justify-content:center;background:#fef3c7;border-radius:4px;min-height:32px;">
          <code style="color:#92400e;font-size:10px;font-family:monospace;">{{${v.name}}}</code>
        </div>
      `,
    });
  });

  // RTE dropdown: insert variable at cursor position inside edited text.
  // Block drag-drop creates a new mj-text; this dropdown inserts INLINE.
  try {
    const rte = editor.RichTextEditor as unknown as {
      add: (name: string, opts: Record<string, unknown>) => void;
      remove: (name: string) => void;
      get: (name: string) => unknown;
    };

    // ─── Remove GrapesJS core "Wrap for style" action ───
    // GrapesJS ships a default `wrap` action (brush/pen icon, tooltip "Wrap
    // for style") that wraps the selected text in a <span> so it can later
    // be targeted by the Style Manager. With our setup admins style word-
    // level text either via the color picker in this toolbar, the sidebar
    // (selection-aware via the router we added) or +Var — the wrap button
    // produces no visible effect and confused admins into thinking it was
    // broken. Remove it so the toolbar only has actions that do something
    // visible.
    if (rte?.remove) rte.remove('wrap');

    // ─── Text color picker (word/sentence-level styling) ───
    // Default MJML plugin RTE toolbar has bold/italic/underline/link but NOT
    // color — admin can't highlight a single word in red/blue/etc.  Native
    // <input type="color"> + execCommand('foreColor') handles this reliably.
    // execCommand is deprecated but still the most compatible way to apply
    // inline styles to a specific text selection inside a contenteditable.
    if (rte?.remove) rte.remove('textColor');
    if (rte?.add) {
      rte.add('textColor', {
        icon: `<input type="color" class="gjs-rte-color" value="#000000"
                 title="Text color (select text first)"
                 style="width:22px;height:22px;border:none;padding:0;
                        background:transparent;cursor:pointer;" />`,
        event: 'input',
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        result: (rteInst: any, action: any) => {
          const input = action?.btn?.querySelector('input[type="color"]') as
            HTMLInputElement | null;
          const color = input?.value;
          if (!color) return;
          // Restore selection (clicking color picker moves focus).
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          const lastRange = (editor as any).__lastRteRange as Range | undefined;
          try {
            const doc = rteInst.doc || rteInst.el?.ownerDocument ||
              editor.Canvas.getDocument();
            const sel = doc?.getSelection?.();
            if (lastRange && sel) {
              sel.removeAllRanges();
              sel.addRange(lastRange);
            }
            // Nothing selected → this becomes the pen colour for the next typing (keepPenColor).
            // Text selected → colour that text only; no pen.
            const has_selection = !!sel && sel.rangeCount > 0 && !sel.getRangeAt(0).collapsed;
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            (editor as any).__penColor = has_selection ? null : color;
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            (editor as any).__penBlockCid = has_selection ? null : editor.getSelected()?.cid ?? null;
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            (editor as any).__penSpot = has_selection || !doc ? null : penCaretSpot(editor, doc);
            // Fallback to execCommand for broadest browser support.
            // Wraps the selected text in <span style="color:...">.
            doc?.execCommand?.('styleWithCSS', false, true);
            doc?.execCommand?.('foreColor', false, color);
            if (doc) syncListMarkerColor(doc, true);
          } catch (e) {
            console.warn('[GrapesEmailEditor] Color apply failed:', e);
          }
        },
      });
    }

    if (rte?.remove) rte.remove('insertVariable');
    if (rte?.add) {
      rte.add('insertVariable', {
        icon: `<select class="gjs-rte-var-select">
            <option value="">+ Var</option>
            ${variables.map((v) => `<option value="${v.name}">${formatVarName(v.name)}</option>`).join('')}
          </select>`,
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        event: 'change',
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        result: (rteInst: any, action: any) => {
          const btnEl = action?.btn as HTMLElement | undefined;
          const select = btnEl?.querySelector('select') as HTMLSelectElement | null;
          const varName = select?.value;
          if (!varName) return;

          // PRIMARY: use the same text-offset approach as drag-drop.
          // __dragVarComp / __dragVarOffset persist past rte:disable so they
          // are valid even after the canvas loses focus to the dropdown.
          // This avoids the DOM Range restore problem: __lastRteRange is only
          // set for non-collapsed selections, so if the user just clicked to
          // position a cursor it is null → insertHTML would land at position 0.
          const savedComp   = (editor as any).__dragVarComp;
          const savedOff    = (editor as any).__dragVarOffset as number | undefined;
          const canvasDoc   = editor.Canvas.getDocument();
          const varTokenStr = `{{${varName}}}`;
          let dropdownInserted = false;

          if (savedComp && typeof savedOff === 'number' && savedOff >= 0 && canvasDoc) {
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            const compEl = (savedComp as any).view?.el as HTMLElement | undefined;
            if (compEl?.isConnected) {
              // eslint-disable-next-line @typescript-eslint/no-explicit-any
              const pos = getInsertPosition(compEl, (editor as any).__dragVarPath, savedOff);
              if (pos) {
                try {
                  const textNode = canvasDoc.createTextNode(varTokenStr);
                  const r = canvasDoc.createRange();
                  r.setStart(pos.container, pos.offset);
                  r.collapse(true);
                  r.insertNode(textNode);
                  const newHtml = compEl.innerHTML;
                  // eslint-disable-next-line @typescript-eslint/no-explicit-any
                  const cAttr: string = (savedComp.get?.('content') || '') as string;
                  if (cAttr) { savedComp.set('content', newHtml); }
                  // eslint-disable-next-line @typescript-eslint/no-explicit-any
                  else { (savedComp as any).components?.(newHtml); }
                  // Advance offset past inserted token so next insert goes after.
                  // eslint-disable-next-line @typescript-eslint/no-explicit-any
                  (editor as any).__dragVarOffset = savedOff + varTokenStr.length;
                  // The rewrite changes the DOM; the offset alone is right for "after the token".
                  // eslint-disable-next-line @typescript-eslint/no-explicit-any
                  (editor as any).__dragVarPath = null;
                  dropdownInserted = true;
                } catch (e) {
                  console.warn('[GrapesEmailEditor] dropdown text-offset insert failed:', e);
                }
              }
            }
          }

          // FALLBACK: restore DOM range and use insertHTML (only if above failed).
          if (!dropdownInserted) {
            const lastRange = (editor as any).__lastRteRange as Range | undefined;
            if (lastRange) {
              try {
                const doc = rteInst.doc || rteInst.el?.ownerDocument || canvasDoc;
                const sel = doc?.getSelection?.();
                if (sel) {
                  sel.removeAllRanges();
                  const collapsed = lastRange.cloneRange();
                  collapsed.collapse(false); // collapse to END — never replace selection
                  sel.addRange(collapsed);
                }
              } catch (e) {
                console.warn('[GrapesEmailEditor] Could not restore RTE cursor:', e);
              }
            }
            rteInst.insertHTML(varTokenStr);
          }
          if (select) select.value = '';
        },
      });
    }

    // ─── Custom Link button (replaces default execCommand+prompt) ───
    // Default link action calls execCommand('createLink') which uses the
    // native window.prompt() for URL input.  Native prompts are sometimes
    // blocked by browser extensions and look poor to non-technical testers.
    // Replace with an inline popup attached to document.body containing a
    // URL field, "Open in new tab" checkbox, and Apply / Cancel / Remove.
    if (rte?.remove) rte.remove('link');
    if (rte?.add) {
      rte.add('link', {
        icon: '<svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round" style="vertical-align:middle;"><path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71"/><path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71"/></svg>',
        attributes: { title: 'Insert / edit link' },
        // Use mousedown so the iframe selection is captured BEFORE focus
        // shifts to the toolbar button.  On 'click' (the default), the
        // browser has already moved focus and the selection is collapsed
        // or pointing at the wrong location → "link goes to wrong place".
        event: 'mousedown',
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        result: (rteInst: any, action: any) => {
          const canvasDoc = editor.Canvas.getDocument();
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          const doc: any = rteInst?.doc || rteInst?.el?.ownerDocument || canvasDoc;
          if (!doc) return;
          // Save the current selection range (collapsed or not) so we can
          // restore it after the popup steals focus.
          let savedRange: Range | null = null;
          let existingAnchor: HTMLAnchorElement | null = null;
          try {
            const sel = doc.getSelection?.();
            if (sel?.rangeCount) {
              savedRange = sel.getRangeAt(0).cloneRange();
            }
            // Fallback to __lastRteRange if current selection is missing.
            if (!savedRange) {
              // eslint-disable-next-line @typescript-eslint/no-explicit-any
              const stored = (editor as any).__lastRteRange as Range | undefined;
              if (stored) savedRange = stored.cloneRange();
            }
            // Detect if the cursor/selection is inside an existing <a>.
            if (savedRange) {
              let n: Node | null = savedRange.startContainer;
              while (n) {
                if (n.nodeType === 1 && (n as HTMLElement).tagName?.toLowerCase() === 'a') {
                  existingAnchor = n as HTMLAnchorElement;
                  break;
                }
                n = n.parentNode;
              }
            }
          } catch (_) { /* ignore */ }

          // Position popup just below the toolbar button.
          const btn = action?.btn as HTMLElement | undefined;
          const r = btn?.getBoundingClientRect();
          const popupX = r ? r.left : 200;
          const popupY = r ? r.bottom + 6 : 200;

          // Remove any pre-existing link popup (in case user double-clicks).
          document.querySelectorAll('[data-gjs-link-popup="1"]').forEach((n) => n.remove());

          const popup = document.createElement('div');
          popup.setAttribute('data-gjs-link-popup', '1');
          popup.style.cssText =
            'position:fixed;z-index:999999;background:#fff;border:1px solid #d1d5db;' +
            'border-radius:6px;padding:12px;box-shadow:0 6px 18px rgba(0,0,0,0.18);' +
            'font-family:Arial,Helvetica,sans-serif;font-size:13px;color:#1f2937;' +
            `min-width:300px;left:${popupX}px;top:${popupY}px;`;

          const initialUrl = existingAnchor?.getAttribute('href') || '';
          const initialNewTab = existingAnchor?.getAttribute('target') === '_blank';
          const escUrl = initialUrl.replace(/"/g, '&quot;');

          popup.innerHTML =
            '<div style="margin-bottom:8px;">' +
            '<label style="display:block;margin-bottom:4px;font-weight:500;">URL</label>' +
            `<input type="url" class="gjs-link-url" placeholder="https://example.com" value="${escUrl}" ` +
            'style="width:100%;padding:6px 8px;border:1px solid #d1d5db;border-radius:4px;box-sizing:border-box;font:inherit;color:inherit;" />' +
            '</div>' +
            '<div style="margin-bottom:10px;">' +
            '<label style="display:flex;align-items:center;cursor:pointer;">' +
            `<input type="checkbox" class="gjs-link-newtab" ${initialNewTab ? 'checked' : ''} style="margin-right:6px;" />` +
            'Open in new tab' +
            '</label>' +
            '</div>' +
            '<div style="display:flex;gap:6px;justify-content:flex-end;">' +
            (existingAnchor
              ? '<button type="button" class="gjs-link-remove" style="padding:5px 10px;background:#fff;color:#dc2626;border:1px solid #dc2626;border-radius:4px;cursor:pointer;font:inherit;">Remove</button>'
              : '') +
            '<button type="button" class="gjs-link-cancel" style="padding:5px 10px;background:#fff;border:1px solid #d1d5db;border-radius:4px;cursor:pointer;font:inherit;">Cancel</button>' +
            '<button type="button" class="gjs-link-apply" style="padding:5px 10px;background:#3b82f6;color:#fff;border:1px solid #3b82f6;border-radius:4px;cursor:pointer;font:inherit;">Apply</button>' +
            '</div>';

          document.body.appendChild(popup);
          const urlInput   = popup.querySelector('.gjs-link-url')    as HTMLInputElement;
          const newTabInp  = popup.querySelector('.gjs-link-newtab') as HTMLInputElement;
          const applyBtn   = popup.querySelector('.gjs-link-apply')  as HTMLButtonElement;
          const cancelBtn  = popup.querySelector('.gjs-link-cancel') as HTMLButtonElement;
          const removeBtn  = popup.querySelector('.gjs-link-remove') as HTMLButtonElement | null;

          requestAnimationFrame(() => {
            urlInput?.focus();
            if (initialUrl) urlInput?.select();
          });

          const restoreSel = () => {
            try {
              const sel = doc.getSelection?.();
              if (savedRange && sel) {
                sel.removeAllRanges();
                sel.addRange(savedRange);
              }
            } catch (_) { /* ignore */ }
          };

          const onOutside = (e: MouseEvent) => {
            if (!popup.contains(e.target as Node)) cleanup();
          };
          const cleanup = () => {
            document.removeEventListener('mousedown', onOutside, true);
            if (popup.parentNode) popup.parentNode.removeChild(popup);
          };
          // Defer outside-listener attach so the click that opened the popup
          // is not caught as an outside click.
          setTimeout(() => document.addEventListener('mousedown', onOutside, true), 0);

          applyBtn?.addEventListener('click', () => {
            const url = urlInput.value.trim();
            if (!url) { urlInput.focus(); return; }
            const newTab = newTabInp.checked;
            restoreSel();
            try {
              if (existingAnchor) {
                existingAnchor.setAttribute('href', url);
                if (newTab) {
                  existingAnchor.setAttribute('target', '_blank');
                  existingAnchor.setAttribute('rel', 'noopener noreferrer');
                } else {
                  existingAnchor.removeAttribute('target');
                  existingAnchor.removeAttribute('rel');
                }
              } else {
                // execCommand('createLink') wraps the selection in <a href>.
                // Requires a non-empty selection — if collapsed, insert the
                // URL as visible text first, then select and link it.
                const sel = doc.getSelection?.();
                if (sel?.rangeCount && sel.getRangeAt(0).collapsed) {
                  const range = sel.getRangeAt(0);
                  const textNode = doc.createTextNode(url);
                  range.insertNode(textNode);
                  range.selectNode(textNode);
                  sel.removeAllRanges();
                  sel.addRange(range);
                }
                doc.execCommand?.('createLink', false, url);
                if (newTab) {
                  const sel2 = doc.getSelection?.();
                  if (sel2?.rangeCount) {
                    let n: Node | null = sel2.getRangeAt(0).startContainer;
                    while (n) {
                      if (n.nodeType === 1 && (n as HTMLElement).tagName?.toLowerCase() === 'a') {
                        (n as HTMLAnchorElement).setAttribute('target', '_blank');
                        (n as HTMLAnchorElement).setAttribute('rel', 'noopener noreferrer');
                        break;
                      }
                      n = n.parentNode;
                    }
                  }
                }
              }
            } catch (e) {
              console.warn('[GrapesEmailEditor] Link apply failed:', e);
            }
            cleanup();
          });

          cancelBtn?.addEventListener('click', cleanup);

          removeBtn?.addEventListener('click', () => {
            if (existingAnchor) {
              try {
                const sel = doc.getSelection?.();
                if (sel) {
                  const range = doc.createRange();
                  range.selectNodeContents(existingAnchor);
                  sel.removeAllRanges();
                  sel.addRange(range);
                }
                doc.execCommand?.('unlink');
              } catch (e) {
                console.warn('[GrapesEmailEditor] Link remove failed:', e);
              }
            }
            cleanup();
          });

          urlInput?.addEventListener('keydown', (e: KeyboardEvent) => {
            if (e.key === 'Enter') {
              e.preventDefault();
              applyBtn?.click();
            } else if (e.key === 'Escape') {
              e.preventDefault();
              cleanup();
            }
          });
        },
      });
    }

    // ─── Bullet list (UL) and Numbered list (OL) ───
    // grapesjs-mjml's default RTE toolbar has bold/italic/underline/link but
    // NO list actions. Tester requested both bullet and numbered lists.
    //
    // execCommand('insertUnorderedList'/'insertOrderedList') wraps selected
    // lines in a <ul>/<ol> with <li> items, but the browser's default UL/OL
    // styling (margin/padding/font reset) makes the resulting list look
    // Fix: snapshot the computed font-size / font-family / color / line-height
    const applyListWithStylePreservation = (rteInst: any, command: 'insertUnorderedList' | 'insertOrderedList') => {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const doc: any = rteInst?.doc || rteInst?.el?.ownerDocument || editor.Canvas.getDocument();
      if (!doc) return;
      // Snapshot current text's effective styles BEFORE the command runs.
      let snap: { fontSize?: string; fontFamily?: string; color?: string; lineHeight?: string; fontWeight?: string } = {};
      try {
        const sel = doc.getSelection?.();
        if (sel && sel.rangeCount > 0) {
          const range = sel.getRangeAt(0);
          let node: Node | null = range.startContainer;
          if (node && node.nodeType === Node.TEXT_NODE) node = node.parentElement;
          if (node && node.nodeType === 1) {
            const cs = doc.defaultView?.getComputedStyle?.(node as Element);
            if (cs) {
              snap = {
                fontSize: cs.fontSize,
                fontFamily: cs.fontFamily,
                color: cs.color,
                lineHeight: cs.lineHeight,
                fontWeight: cs.fontWeight,
              };
            }
          }
        }
      } catch { /* ignore — fall through to bare execCommand */ }

      try { doc.execCommand?.(command, false, undefined); } catch { return; }

      // Locate the new <ul>/<ol> via the post-command selection and re-apply
      // the captured styles inline so the list visually matches the surrounding text.
      try {
        const sel = doc.getSelection?.();
        if (!sel || sel.rangeCount === 0) return;
        let n: Node | null = sel.getRangeAt(0).commonAncestorContainer;
        // Walk up to find the wrapping UL or OL.
        while (n) {
          if (n.nodeType === 1) {
            const tag = (n as HTMLElement).tagName.toLowerCase();
            if (tag === 'ul' || tag === 'ol') break;
          }
          n = n.parentNode;
        }
        if (!n || n.nodeType !== 1) return;
        const list = n as HTMLElement;
        const setIfMissing = (el: HTMLElement, prop: string, val?: string) => {
          if (!val) return;
          // Only set if not already declared inline (don't overwrite user's
          // intentional per-li styling on subsequent calls).
          if (!el.style.getPropertyValue(prop)) el.style.setProperty(prop, val);
        };
        // Line-height as a ratio (min 1.5): a frozen px value equal to the font size left no gap between lines.
        const font_px = parseFloat(snap.fontSize || '');
        const line_px = parseFloat(snap.lineHeight || '');
        const ratio = font_px > 0 && line_px > 0 ? line_px / font_px : 0;
        const list_line_height = String(Math.max(Math.round(ratio * 100) / 100, 1.5));
        setIfMissing(list, 'font-size', snap.fontSize);
        setIfMissing(list, 'font-family', snap.fontFamily);
        setIfMissing(list, 'color', snap.color);
        setIfMissing(list, 'line-height', list_line_height);
        // padding-left default keeps bullets/numbers visible; don't strip it.
        list.querySelectorAll('li').forEach((li) => {
          setIfMissing(li, 'font-size', snap.fontSize);
          setIfMissing(li, 'font-family', snap.fontFamily);
          setIfMissing(li, 'color', snap.color);
          setIfMissing(li, 'line-height', list_line_height);
          setIfMissing(li, 'font-weight', snap.fontWeight);
        });
        // Chrome copies the canvas grey onto spans; it turns black in dark-mode email clients.
        list.querySelectorAll('span').forEach((span) => {
          if (span.style.backgroundColor.replace(/\s/g, '') === 'rgb(250,250,250)') {
            span.style.removeProperty('background-color');
            if (!span.getAttribute('style')) span.removeAttribute('style');
          }
        });
      } catch (e) {
        console.warn('[GrapesEmailEditor] List style preservation failed:', e);
      }
    };

    if (rte?.remove) rte.remove('bulletList');
    if (rte?.add) {
      rte.add('bulletList', {
        // Three horizontal lines with bullet dots — universally readable as "UL"
        icon: '<svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" style="vertical-align:middle;"><circle cx="4" cy="6" r="1.2" fill="currentColor"/><circle cx="4" cy="12" r="1.2" fill="currentColor"/><circle cx="4" cy="18" r="1.2" fill="currentColor"/><line x1="9" y1="6" x2="20" y2="6"/><line x1="9" y1="12" x2="20" y2="12"/><line x1="9" y1="18" x2="20" y2="18"/></svg>',
        attributes: { title: 'Bullet list' },
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        result: (rteInst: any) => applyListWithStylePreservation(rteInst, 'insertUnorderedList'),
      });
    }

    if (rte?.remove) rte.remove('numberedList');
    if (rte?.add) {
      rte.add('numberedList', {
        icon: '<svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" style="vertical-align:middle;"><text x="2" y="8" font-size="6" font-weight="700" fill="currentColor" stroke="none">1</text><text x="2" y="14" font-size="6" font-weight="700" fill="currentColor" stroke="none">2</text><text x="2" y="20" font-size="6" font-weight="700" fill="currentColor" stroke="none">3</text><line x1="9" y1="6" x2="20" y2="6"/><line x1="9" y1="12" x2="20" y2="12"/><line x1="9" y1="18" x2="20" y2="18"/></svg>',
        attributes: { title: 'Numbered list' },
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        result: (rteInst: any) => applyListWithStylePreservation(rteInst, 'insertOrderedList'),
      });
    }
  } catch (err) {
    console.warn('[GrapesEmailEditor] Could not register RTE insertVariable:', err);
  }
};

// ============== CURSOR TEXT-OFFSET HELPERS ==============
// Used by drag-drop variable insertion to locate the caret position in a
// re-rendered DOM.  Range objects store stale node references after
// MJML compilation re-renders mj-text internals; character offsets are
// stable because the text content doesn't change during compilation.

function getTextOffset(root: HTMLElement, targetNode: Node, targetOffset: number): number {
  const doc = root.ownerDocument;
  if (!doc) return -1;
  const walker = doc.createTreeWalker(root, NodeFilter.SHOW_TEXT, null);
  let count = 0;
  let node: Node | null = walker.nextNode();
  while (node) {
    if (node === targetNode) return count + targetOffset;
    count += node.textContent?.length ?? 0;
    node = walker.nextNode();
  }
  return -1; // targetNode is not inside root
}

function getNodeAtTextOffset(
  root: HTMLElement,
  targetOffset: number,
): { container: Node; offset: number } | null {
  if (targetOffset < 0) return null;
  const doc = root.ownerDocument;
  if (!doc) return null;
  const walker = doc.createTreeWalker(root, NodeFilter.SHOW_TEXT, null);
  let count = 0;
  let node: Node | null = walker.nextNode();
  let last: Node | null = null;
  while (node) {
    last = node;
    const len = node.textContent?.length ?? 0;
    if (count + len >= targetOffset) return { container: node, offset: targetOffset - count };
    count += len;
    node = walker.nextNode();
  }
  // Cursor was at/past end — append after the last text node.
  if (last) return { container: last, offset: last.textContent?.length ?? 0 };
  return null;
}

// Characters before a caret that may sit in an element (e.g. an empty line), not a text node.
function getCharOffset(root: HTMLElement, node: Node, offset: number): number {
  try {
    if (!root.contains(node)) return -1;
    const r = root.ownerDocument.createRange();
    r.setStart(root, 0);
    r.setEnd(node, offset);
    return r.toString().length;
  } catch { return -1; }
}

// Caret as child indexes from root + offset. Exact even on empty lines and line edges.
function getCaretPath(root: HTMLElement, node: Node, offset: number): number[] | null {
  const path: number[] = [offset];
  let n: Node | null = node;
  while (n && n !== root) {
    const parent: Node | null = n.parentNode;
    if (!parent) return null;
    path.unshift(Array.prototype.indexOf.call(parent.childNodes, n));
    n = parent;
  }
  return n === root ? path : null;
}

// Where to insert a variable: the exact saved spot when it still matches the saved
// character offset (same text after a re-render); otherwise the old offset-only lookup.
function getInsertPosition(
  root: HTMLElement,
  path: number[] | null | undefined,
  char_offset: number,
): { container: Node; offset: number } | null {
  if (path && path.length) {
    let n: Node | undefined = root;
    for (let i = 0; i < path.length - 1 && n; i++) n = n.childNodes[path[i]];
    const off = path[path.length - 1];
    if (n) {
      const max = n.nodeType === 3 ? (n.textContent?.length ?? 0) : n.childNodes.length;
      if (off <= max && getCharOffset(root, n, off) === char_offset) return { container: n, offset: off };
    }
  }
  return getNodeAtTextOffset(root, char_offset);
}

// ============== MAIN COMPONENT ==============

export default function GrapesEmailEditor({
  value,
  onChange,
  editorJson,
  onJsonChange,
  variables,
  minHeight = '700px',
  error,
  label,
  required = false,
  onEditorReady,
}: GrapesEmailEditorProps) {
  const editorRef = useRef<Editor | null>(null);
  const isLoadedRef = useRef(false);
  // Pending debouncedExtract timer — held in a ref so the unmount effect can
  // cancel it. Without this cancel, a timer scheduled in the last 500ms before
  // the user navigates away fires AFTER React has unmounted GrapesEmailEditor
  // and GrapesJS has called editor.destroy(). The callback then touches
  // editor.UndoManager on a destroyed editor → UndoManager is undefined →
  // "cannot read properties of undefined (reading 'stop')" in Sentry, with
  // the URL captured as /admin/enterprise/email-templates (the list page the
  // user has already navigated to).
  const debounceTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // ── Snapshot-based Undo/Redo (replaces broken GrapesJS UndoManager) ──
  const undoStackRef = useRef<any[]>([]);
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const redoStackRef = useRef<any[]>([]);
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const currentStateRef = useRef<any>(null);
  const isApplyingHistoryRef = useRef(false);
  const historyDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  // Shared RTE-active flag — written by initEditor's rte:enable/disable handlers,
  // read by the snapshot useEffect (separate closure) to gate captureSnapshot.
  const rteActiveRef = useRef(false);

  // Stable refs for callbacks — GrapesJS event handlers capture closures once,
  // refs ensure they always call the latest parent functions.
  const onChangeRef = useRef(onChange);
  const onJsonChangeRef = useRef(onJsonChange);
  const onEditorReadyRef = useRef(onEditorReady);
  const variablesRef = useRef(variables);
  onChangeRef.current = onChange;
  onJsonChangeRef.current = onJsonChange;
  onEditorReadyRef.current = onEditorReady;
  variablesRef.current = variables;

  // Initial-value refs — captured at mount OR the first time value/editorJson
  // becomes non-empty (templates often fetch async, so they're empty on first
  // render).  onReady reads from these, NOT from the live prop, so user typing
  // doesn't trigger a re-load that would blow away the cursor position.
  const initialValueRef = useRef(value);
  const initialJsonRef = useRef(editorJson);
  useEffect(() => {
    if (!initialValueRef.current && value) initialValueRef.current = value;
    if (!initialJsonRef.current && editorJson) initialJsonRef.current = editorJson;
  }, [value, editorJson]);

  // ── Unmount cleanup: cancel pending callbacks AND tear down GrapesJS
  //    drag/scroll listeners BEFORE the editor is destroyed ──
  //
  // Without this:
  // A debounce timer scheduled within 500ms of navigation away fires
  //     AFTER editor.destroy() and touches editor.UndoManager (undefined),
  //     causing the original Sentry crash logged on the email-templates list.
  //  GrapesJS's autoscroll Sorter attaches scroll listeners on the canvas
  //     iframe + the host document during drag-and-drop.  If the user starts
  //     a drag (or even just a quick mousedown that arms the sorter) and
  //     then navigates away, those listeners survive editor.destroy() until
  // Empty deps → effect registers once; cleanup runs exactly once at unmount.
  useEffect(() => {
    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
        debounceTimerRef.current = null;
      }
      if (historyDebounceRef.current) {
        clearTimeout(historyDebounceRef.current);
        historyDebounceRef.current = null;
      }
      // Force-stop any in-progress drag/sort operations so their attached
      // scroll listeners are removed before @grapesjs/react calls destroy.
      const ed = editorRef.current as any;
      if (ed) {
        try {
          // Only stop commands that are actually registered — calling
          // stopCommand on an unknown id triggers GrapesJS's internal
          // logWarning ("`<id>` command not found"), which Sentry surfaces
          // as a warning event.
          const cmds = ed.Commands;
          const safeStop = (id: string) => {
            try {
              if (cmds?.get?.(id) && cmds.isActive?.(id)) cmds.stop(id);
            } catch (_) { /* ignore — command may be mid-tear-down */ }
          };
          safeStop('tlb-move');
          safeStop('tlb-clone');
          safeStop('core:component-drag');
          // Cancel the sorter directly: setting dragging:false and triggering
          // dragend forces the sorter to remove its scroll listeners.
          ed.Canvas?.getModel?.()?.set?.('dragging', false);
          ed.trigger?.('canvas:dragend');
          ed.trigger?.('sorter:drag:end');
        } catch (_) { /* editor may already be partly torn down — ignore */ }
      }
    };
  }, []);

  /**
   * Compile MJML → HTML and extract project JSON, send both to parent form.
   */
  const extractAndNotify = useCallback((editor: Editor) => {
    try {
      // Defensive runCommand: if editor.Commands is missing for any reason
      // (mid-init, mid-teardown, plugin not yet loaded), runCommand internally
      // dereferences `undefined.run`.  Catch that here and bail cleanly so
      // the surrounding try/finally still restarts UndoManager.
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const cmds: any = (editor as any).Commands;
      if (!cmds || typeof cmds.run !== 'function') {
        // Demoted from warn → debug: this is the expected path when a
        // debounced extract fires after editor teardown has begun.  The
        // surrounding cleanup already cancels pending timers, but a
        // millisecond-level race can still queue one extra call.
        if (typeof console.debug === 'function') {
          console.debug('[GrapesEmailEditor] extractAndNotify: editor.Commands not ready, skipping');
        }
        return;
      }
      let result: any;
      try {
        result = cmds.run('mjml-code-to-html');
      } catch (cmdErr) {
        console.warn('[GrapesEmailEditor] mjml-code-to-html threw, skipping extract:', cmdErr);
        return;
      }
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const wrapper = (editor as any).DomComponents?.getWrapper?.();
      const childCount = wrapper?.components?.()?.length ?? 0;
      // Safety gate: MJML compilation sometimes returns only wrapper HTML with
      // no real body content (observed when editor state is in flux during a
      // trait update).  Writing that empty HTML to formData overwrites the
      // admin's real template.  Reject the output if it has no substantive
      // content — formData keeps its previous value, admin's real HTML is safe.
      if (result?.html && hasRealEmailBody(result.html)) {
        onChangeRef.current(result.html);
      } else if (result?.html) {
        console.warn(
          '[GrapesEmailEditor] Compiled HTML has no real body content, skipping onChange',
          { length: result.html.length, tree_children: childCount },
        );
      }

      // CRITICAL: mark descendants non-selectable BEFORE saving JSON.
      // mjml-code-to-html re-renders mj-text internals — fresh <td>/<tr>/<div>
      // nodes arrive without `selectable:false`. If we called getProjectData()
      // first, the saved JSON would contain selectable:true on those nodes; on
      // the next load they appear as clickable "Table cell" components and the
      // RTE toolbar never activates.  Correct order: mark → expand → save.
      markMjTextDescendants(editor);
      // Same reason: compile rebuilds mj-text's inner subtree; fresh nodes
      // arrive without the widened stylable list.
      expandStylableAcrossTree(editor);

      // editor_json is always safe to sync — it's a structural snapshot,
      // not a compiled output that can degrade.
      onJsonChangeRef.current?.(editor.getProjectData());
    } catch (err) {
      console.error('[GrapesEmailEditor] Failed to extract HTML:', err);
    }
  }, []);

  /** Heuristic: does the compiled HTML have a non-empty body? */
  function hasRealEmailBody(html: string): boolean {
    const bodyMatch = html.match(/<body[^>]*>([\s\S]*)<\/body>/i);
    if (!bodyMatch) return true; // no body tag; trust it (not our typical case)
    const body = bodyMatch[1];
    // Strip all tags, collapse whitespace
    const text = body.replace(/<[^>]+>/g, '').replace(/\s+/g, ' ').trim();
    const hasVariables = /\{\{/.test(body);
    const hasImages = /<img\s/i.test(body);
    const hasLinks = /<a\s[^>]*href/i.test(body);
    // Substantive if there's readable text, or any variable/image/link
    return text.length > 20 || hasVariables || hasImages || hasLinks;
  }

  /**
   * Called when Editor instance is created (before canvas mount).
   * Register change listeners here — they persist for the editor's lifetime.
   * Debounced at 500ms to avoid excessive MJML compilation.
   */
  const onEditor = useCallback((editor: Editor) => {
    editorRef.current = editor;
    onEditorReadyRef.current?.(editor);

    // ── Fix mj-column drop-zone precision ─────────────────────────────
    // grapesjs-mjml defines mj-column.getChildrenSelector() → 'table'.
    // mj-text components render as <tr> elements inside <table><tbody>.
    // The GrapesJS sorter looks for sortable items inside the children
    // container; with container='table' it only finds <tbody> (one item),
    // so it shows a single indicator at the top or bottom of the column.
    // Pointing it to 'table > tbody' makes the sorter see the <tr> rows
    // directly and render a drop line BETWEEN each mj-text block. ✓
    //
    // Note: mj-body uses getChildrenSelector → 'div' which also has a
    // similar issue (resolves to the first mj-section after render).
    // However, overriding mj-body's getChildrenSelector causes a blank
    // canvas because coreMjmlView.render() sets this.el.innerHTML to a
    // minimal skeleton, then uses getChildrenContainer() to re-attach
    // child view els. With null → this.el as container, the rerender
    // path orphans the compiled section DOM nodes and mjml-code-to-html
    // loses its component-to-DOM mapping → blank canvas.
    // Section-level (1-Col/2-Col/3-Col) drop precision is instead handled
    // via the block:drag:stop correction below.
    editor.Components.addType('mj-column', {
      view: {
        getChildrenSelector() {
          return 'table > tbody';
        },
      },
    });

    // ════════════════════════════════════════════════════════════════════
    //   UNIFIED BACKGROUND-COLOR HANDLER
    // ════════════════════════════════════════════════════════════════════

    const BG_ATTR_TYPES = new Set([
      'mj-section',
      'mj-column',
      'mj-wrapper',
      'mj-hero',
      'mj-body',
    ]);
    const CONTAINER_BG_TYPES = new Set([
      'mj-text',
      'mj-button',
      'mj-image',
      'mj-spacer',
      'mj-divider',
      'mj-navbar-link',
      'mj-social-element',
    ]);
    const PROPAGATE_TO_CARD_CHILDREN = new Set(['mj-section', 'mj-column']);

    const lastBg = new WeakMap<object, string>();

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    editor.on('component:selected', (comp: any) => {
      if (!comp || typeof comp.get !== 'function') return;
      const type = (comp.get('type') || '').toString();
      if (!BG_ATTR_TYPES.has(type) && !CONTAINER_BG_TYPES.has(type)) return;
      const bg = ((comp.getStyle?.() || {})['background-color'] || '') as string;
      lastBg.set(comp, bg);
    });

    // EVENT CHOICE:
    //   `style:property:update` fires DURING Style Manager's write cycle —
    //   before the value reaches component.style. Reading at that instant
    //   returns the stale style (`{vertical-align: 'top'}` for a freshly-
    //   picked color), so our handler would miss the change. Previous
    //   attempts used setTimeout to defer the read, but timing is flaky —
    //   a too-short wait races the write, a too-long wait feels laggy.
  
    // DEBOUNCE:
    //   During a color-picker drag, component:styleUpdate fires many
    //   times. A 30 ms debounce coalesces them into one propagation with
    //   the final color. Rapid two-click changes (pick A then B quickly)
    //   also coalesce — the timer resets on every new event, so only the
    //   final settled style gets propagated. 30 ms is imperceptible.
    let bgDebounceTimer: ReturnType<typeof setTimeout> | null = null;

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const runBgCheck = (c: any) => {
      try {
        const type = (c.get?.('type') || '').toString();
        if (!BG_ATTR_TYPES.has(type) && !CONTAINER_BG_TYPES.has(type)) return;

        const style = c.getStyle?.() || {};
        const currentBg = (style['background-color'] || '') as string;
        const previousBg = lastBg.get(c);
        // Always propagate on first encounter (previousBg === undefined) — the
        // earlier "seed-and-return" caused the user-reported "first colour pick
        // doesn't apply" bug: when grapesjs-mjml replaces the component
        // reference (after MJML recompile), the WeakMap lookup misses, so the
        // very first runBgCheck for the new ref returned without mirroring the
        // style → MJML attribute, leaving the canvas un-updated until the
        // SECOND pick.  Propagation is idempotent (no DOM change when values
        // already match) so always running it is safe.
        lastBg.set(c, currentBg);
        if (previousBg !== undefined && previousBg === currentBg) return;

        // ── UndoManager grouping ────────────────────────────────────────
        // The user's primary action (picking a bg color) is already
        // recorded in UndoManager BEFORE we get here (styleUpdate fires
        // AFTER the style write).  Everything we do below is derivative
        // propagation — setStyle on inner divs, set('content', ...) on
        // mj-text nodes, addAttributes for container-bg.  Each would
        // normally pile up as its own undo entry, so a single Undo would
        // revert only the last propagation write and leave the editor in
        // an inconsistent state.
    
        const um: any = editor.UndoManager;
        const propagate = () => {
        // ── Button detector ─────────────────────────────────────────────
        // Regular inline links never set `display: block` or
        // `display: inline-block` on themselves — that property turns the
        // <a> into a clickable rectangle, which is what every "button" in
        // these email templates is.  So the simplest reliable signature:
        //   <a style="...display: block...">          OR
        //   <a style="...display: inline-block...">   OR
        //   <a style="...background[-color]:...">     (a styled link with
        //                                              its own bg — also

        const isStyledButtonAnchor = (a: any): boolean => {
          if (!a || (a.tagName || '').toLowerCase() !== 'a') return false;
          const s = a.getAttribute ? a.getAttribute('style') || '' : '';
          return /display\s*:\s*(?:inline-)?block|background(?:-color)?\s*:/i.test(s);
        };
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const isButtonElement = (el: any): boolean => {
          if (!el) return false;
          const tag = (el.tagName || '').toLowerCase();
          // Rule 1: the <a> button itself.
          if (isStyledButtonAnchor(el)) return true;
          // Rule 2: a <td>/<th> button cell (direct or shallow-wrapped <a>).
          if (tag === 'td' || tag === 'th') {
            // querySelectorAll is fine here — we only check the cell itself,
            // and bail at the first styled <a> we find.
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            const anchors: any[] = el.querySelectorAll
              ? Array.from(el.querySelectorAll('a'))
              : [];
            for (const a of anchors) {
              if (isStyledButtonAnchor(a)) return true;
            }
          }
          return false;
        };

        // ── Helper: rewrite inner HTML, updating bg-color on all elements
        // EXCEPT button subtrees (MJML button compile output).  Buttons
        // own their full visual color across <a>/<td>/<table>; touching
        // any of them recolors the button.  Skipping the entire subtree
        // is the only reliable way to preserve the button.
        // Returns the new HTML string, or the original if nothing changed.
        const rewriteInnerBgPreservingButtons = (
          source: string,
          newBg: string,
        ): string => {
          if (!source || typeof source !== 'string') return source;
          if (typeof DOMParser === 'undefined') return source;
          try {
            const doc = new DOMParser().parseFromString(
              `<div data-bgroot>${source}</div>`,
              'text/html',
            );
            const root = doc.querySelector('[data-bgroot]');
            if (!root) return source;
            let changed = false;
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            const walkEl = (el: any) => {
              // Skip the entire subtree if it is or contains an MJML button.
              if (isButtonElement(el)) return;
              // Update inline style.background-color
              const styleAttr = el.getAttribute('style') || '';
              if (/background-color\s*:/i.test(styleAttr)) {
                const next = newBg
                  ? styleAttr.replace(
                      /background-color\s*:\s*[^;"']+/gi,
                      `background-color: ${newBg}`,
                    )
                  : styleAttr.replace(
                      /background-color\s*:\s*[^;"']+;?/gi,
                      '',
                    );
                if (next !== styleAttr) {
                  el.setAttribute('style', next);
                  changed = true;
                }
              }
              // Update bgcolor attribute (for legacy <th>/<td>)
              if (el.hasAttribute('bgcolor')) {
                if (newBg) el.setAttribute('bgcolor', newBg);
                else el.removeAttribute('bgcolor');
                changed = true;
              }
              for (const child of Array.from(el.children)) {
                walkEl(child);
              }
            };
            for (const child of Array.from(root.children)) {
              walkEl(child);
            }
            return changed ? root.innerHTML : source;
          } catch {
            return source;
          }
        };

        //
        //   IMPORTANT: do NOT call removeAttributes('background-color').
        //   Empirically confirmed via console logs: that removal triggers
        //   grapesjs-mjml's handleAttributeChange → setStyle(attributes)
        //   → style loses background-color → component:styleUpdate fires
        //   again with empty style → our handler propagates the empty value
        //   and the inner card goes back to its previous color. Feedback
        //   loop. Leaving the redundant attribute is harmless: MJML compile
        //   ignores `background-color` on these tags, and the correctly-
        //   named `container-background-color` does the work.
        if (CONTAINER_BG_TYPES.has(type)) {
          if (currentBg) {
            c.addAttributes({ 'container-background-color': currentBg });
          } else {
            c.removeAttributes('container-background-color');
          }
          // The user explicitly selected THIS component and changed its bg —
          // they expect the whole visual block to recolor (the container's
          // wrapping <td>, AND any inner card-style <div>/<th>/<td> with
          // their own inline bg).  Walk the inner HTML and update every
          // bg-color we find, except inside <a> tags so any inline
          // links/buttons keep their own color.
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          const cView = (c as any).view;
          const cEl = cView?.el as HTMLElement | undefined;
          const cContentAttr: string = (c.get?.('content') || '') as string;
          const cInnerHtml: string = cEl?.innerHTML || '';
          const cSource = cContentAttr || cInnerHtml;
          if (cSource) {
            const cUpdated = rewriteInnerBgPreservingButtons(cSource, currentBg);
            if (cUpdated !== cSource) {
              if (cContentAttr) {
                c.set('content', cUpdated);
              } else {
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                (c as any).components?.(cUpdated);
              }
            }
          }
        }

        // ── (2) Propagate to card children for section/column ──
        //   htmlToMjml's preserveCellStyles wraps preserved card styles in
        //   `<div style="background-color:...">...</div>`. That inner div
        //   visually covers the section's/column's new bg. Walk the subtree
        //   and update any such inline bg so the visible card matches.
        if (PROPAGATE_TO_CARD_CHILDREN.has(type)) {
          let structuredDivs = 0;
          let stringContent = 0;

          // Inline-bg-host tags (any of these with an inline
          // background-color are visual card/header wrappers that would
          // otherwise paint over the new section/column color).
          const INLINE_BG_HOSTS = new Set([
            'div',
            'td',
            'th',
            'tr',
            'tbody',
            'thead',
            'table',
            'span',
            'p',
          ]);

          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          const walk = (node: any) => {
            const tag = (node.get?.('tagName') || '').toString().toLowerCase();
            const nType = (node.get?.('type') || '').toString();

            // Stop at nested MJML components that own their own bg-color
            // (mj-button, mj-image, mj-divider, mj-spacer, mj-social-element,
            // mj-navbar-link).  These are independent visual elements — their
            // bg must NOT be overwritten by the parent section/column's bg.
            // Without this guard, the walk descended into mj-button's compiled
            // <table>/<td> children and Path A repainted them, turning the
            // button the same color as the section.  mj-text is the exception:
            // legacy templates store inline bg-color on inner <div>/<td>
            // wrappers that we DO want to keep in sync (handled by Path B).
            if (node !== c && CONTAINER_BG_TYPES.has(nType) && nType !== 'mj-text') {
              return;
            }

            // Path A — any structured inline-bg-host component with inline bg.
            // Update unconditionally so legacy card-style wrappers and table
            // headers track the new section/column bg.  The walk-guard above
            // already skips real <mj-button>/<mj-image> components, so they
            // never reach this update.
            if (INLINE_BG_HOSTS.has(tag)) {
              const innerStyle = { ...(node.getStyle?.() || {}) };
              if ('background-color' in innerStyle) {
                if (currentBg) innerStyle['background-color'] = currentBg;
                else delete innerStyle['background-color'];
                node.setStyle(innerStyle);
                structuredDivs += 1;
              }
            }

            // Path B — mj-text inner content (from `content` attribute OR
            // live view.el.innerHTML).  Always run rewriteInnerBgPreservingButtons:
            // the helper walks the DOM and updates every inline bg-color /
            // bgcolor EXCEPT inside button subtrees.  Crucially, we do NOT
            // pre-gate on isButtonHtml(source) — that would skip the WHOLE
            // mj-text whenever ANY button exists inside it (e.g. a course
            // table mj-text that contains both a header row AND a Start
            // button: pre-gating made the header row never update).  The
            // helper handles button skipping on a per-subtree basis.
            if (nType === 'mj-text') {
              // eslint-disable-next-line @typescript-eslint/no-explicit-any
              const nView = (node as any).view;
              // eslint-disable-next-line @typescript-eslint/no-explicit-any
              const nEl = nView?.el as HTMLElement | undefined;
              const contentAttr: string = (node.get?.('content') || '') as string;
              const innerHtml: string = (nEl?.innerHTML || '');
              const source = contentAttr || innerHtml;
              if (typeof source === 'string' && source) {
                const updated = rewriteInnerBgPreservingButtons(source, currentBg);
                if (updated !== source) {
                  if (contentAttr) {
                    node.set('content', updated);
                  } else {
                    // eslint-disable-next-line @typescript-eslint/no-explicit-any
                    (node as any).components?.(updated);
                  }
                  stringContent += 1;
                }
              }
            }

            node.components?.()?.each?.(walk);
          };
          walk(c);
        }
        }; // ← end of propagate()
        // Run propagation under UndoManager.skip if available, plain call
        // otherwise.  If skip is missing, propagation will pile up undo
        // entries but the editor still works — fail-soft.
        if (um && typeof um.skip === 'function') {
          um.skip(propagate);
        } else {
          propagate();
        }
      } catch (err) {
        console.warn('[GrapesEmailEditor] unified bg-color handler failed:', err);
      }
    };

    // Hook into `component:styleUpdate` — fires AFTER style write, so
    // comp.getStyle() in runBgCheck returns the canonical new value.
    // Debounce 30 ms so drag/rapid-click bursts run exactly once.
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    editor.on('component:styleUpdate', (comp: any) => {
      if (!comp || typeof comp.get !== 'function') return;
      const type = (comp.get('type') || '').toString();
      if (!BG_ATTR_TYPES.has(type) && !CONTAINER_BG_TYPES.has(type)) return;
      // If text is selected in the RTE inside an mj-text, the inline-text handler
      // (registered below) will wrap the selection in a <span style="background-color:…">
      // instead.  Skip the whole-block container-background-color update here so
      // the two handlers don't fight each other.
      //
      // Use __rteTextSelection (sticky) rather than __lastRteRangeText so the
      // guard keeps blocking even after the first inline apply clears
      // __lastRteRangeText — the user's intent remains "inline text highlight"
      // until they click away from the mj-text (component:deselected).
      const rteSelection = (
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        ((editor as any).__rteTextSelection as string | undefined) ||
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        ((editor as any).__lastRteRangeText as string | undefined)
      );
      if (rteSelection && rteSelection.trim() && CONTAINER_BG_TYPES.has(type)) return;
      // Skip bg propagation while the RTE is active — runBgCheck calls
      // node.set('content', ...) which rebuilds the contenteditable DOM and
      // destroys the cursor position.  The propagation runs on rte:disable instead.
      if (rteActive) return;
      if (bgDebounceTimer) clearTimeout(bgDebounceTimer);
      bgDebounceTimer = setTimeout(() => {
        bgDebounceTimer = null;
        runBgCheck(comp);
      }, 30);
    });

    // ════════════════════════════════════════════════════════════════════
    //   BUTTON-UNIT BACKGROUND-COLOR HANDLER
    // ════════════════════════════════════════════════════════════════════
    // When a button-unit (a styled <a>, a <button>, or a <table>-wrapped
    // button — promoted by markMjTextDescendants) gets a new bg-color from
    // the Decorations sector, rewrite EVERY background-color/bgcolor inside
    // the button subtree so the visible button matches the picked colour.
    //
    // CRUCIALLY this handler is the INVERSE of rewriteInnerBgPreservingButtons:
    // that one preserves buttons while recolouring the surrounding text;
    // this one recolours the button while leaving the surrounding mj-text
    // wrapper alone.  The two paths never collide because they're gated on
    // mutually-exclusive component selections:
    //   • mj-text selection  → unified bg handler (skips button subtrees)
    //   • button-unit select → this handler          (only touches button subtree)
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const lastButtonBg = new WeakMap<object, string>();

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    editor.on('component:selected', (comp: any) => {
      if (!comp?.get?.('__buttonUnit')) return;
      const bg = ((comp.getStyle?.() || {})['background-color'] || '') as string;
      lastButtonBg.set(comp, bg);
    });

    let buttonBgDebounce: ReturnType<typeof setTimeout> | null = null;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    editor.on('component:styleUpdate', (comp: any) => {
      if (!comp?.get?.('__buttonUnit')) return;
      if (buttonBgDebounce) clearTimeout(buttonBgDebounce);
      buttonBgDebounce = setTimeout(() => {
        buttonBgDebounce = null;
        try {
          const style = comp.getStyle?.() || {};
          const currentBg = (style['background-color'] || '') as string;
          const previousBg = lastButtonBg.get(comp);
          lastButtonBg.set(comp, currentBg);
          if (previousBg !== undefined && previousBg === currentBg) return;
          if (!currentBg) return;

          // Find the enclosing mj-text so we can write the HTML back if it
          // uses content-attribute mode.  In children-mode mj-text, model
          // mutations alone are enough — the view re-renders from children.
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          let mjTextParent: any = comp.parent?.();
          while (mjTextParent && (mjTextParent.get?.('type') || '') !== 'mj-text') {
            mjTextParent = mjTextParent.parent?.();
          }

          const um: any = editor.UndoManager;
          const applyRecolor = () => {
            // ── Pass 1: walk Backbone subtree, set bg style + bgcolor attr ─
            // setStyle keeps the model and the view in sync; addAttributes
            // covers the legacy bgcolor attribute on <td>/<th> that some
            // mail clients honour over CSS.
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            const walkComp = (c: any) => {
              if (!c?.get) return;
              const tg = (c.get('tagName') || '').toString().toLowerCase();
              // Update bg-color on every visible HTML node in the button.
              // Skip <br>/<hr>/text-nodes — they have no painted area.
              if (tg && tg !== 'br' && tg !== 'hr') {
                try {
                  const cur = (c.getStyle?.() || {}) as Record<string, string>;
                  c.setStyle?.({ ...cur, 'background-color': currentBg });
                  const attrs = c.getAttributes?.() || {};
                  if (attrs.bgcolor !== undefined) {
                    c.addAttributes?.({ bgcolor: currentBg });
                  } else if (tg === 'td' || tg === 'th' || tg === 'table') {
                    // Add bgcolor on table cells/tables that didn't have one —
                    // mirrors how MJML's button compile emits bgcolor for
                    // bulletproof email-client support.
                    c.addAttributes?.({ bgcolor: currentBg });
                  }
                } catch (_) { /* per-node failure must not break the walk */ }
              }
              c.components?.()?.each?.(walkComp);
            };
            walkComp(comp);

            // ── Pass 2: also rewrite the live DOM so the visible button
            // updates immediately, even before any MJML recompile.  The DOM
            // rewrite is a belt-and-braces fallback for the case where a
            // descendant exists only as raw innerHTML (not as a Backbone
            // child), which happens in content-attribute mj-text mode.
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            const btnEl = (comp as any).view?.el as HTMLElement | undefined;
            if (btnEl) {
              const walkDom = (n: HTMLElement) => {
                const styleAttr = n.getAttribute('style') || '';
                if (/background(-color)?\s*:/i.test(styleAttr)) {
                  const next = styleAttr.replace(
                    /background(?:-color)?\s*:\s*[^;"']+;?/gi,
                    '',
                  ).replace(/;?\s*$/, '') + `; background-color: ${currentBg};`;
                  n.setAttribute('style', next.replace(/^[\s;]+/, ''));
                } else {
                  const cur = n.getAttribute('style') || '';
                  n.setAttribute(
                    'style',
                    (cur ? cur.replace(/;?\s*$/, '') + '; ' : '') +
                    `background-color: ${currentBg}`,
                  );
                }
                if (n.hasAttribute('bgcolor')) n.setAttribute('bgcolor', currentBg);
                Array.from(n.children).forEach((c) => walkDom(c as HTMLElement));
              };
              walkDom(btnEl);

              // ── Pass 3: if parent mj-text uses content-attribute mode,
              // serialize the live (now-recoloured) DOM back into content so
              // the change survives the next mjml-code-to-html compile.
              if (mjTextParent) {
                const contentAttr = (mjTextParent.get?.('content') || '') as string;
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                const pEl = (mjTextParent as any).view?.el as HTMLElement | undefined;
                if (contentAttr && pEl) {
                  mjTextParent.set('content', pEl.innerHTML);
                }
              }
            }
          };

          // Group all writes into ONE undo step — same pattern as runBgCheck.
          if (um?.skip) um.skip(applyRecolor); else applyRecolor();
        } catch (err) {
          console.warn('[GrapesEmailEditor] button-unit bg handler failed:', err);
        }
      }, 30);
    });

    // ════════════════════════════════════════════════════════════════════
    //   BUTTON-UNIT TYPOGRAPHY HANDLER
    // ════════════════════════════════════════════════════════════════════
    // Sister to the bg handler above — when the user changes a typography
    // property (text color, font-size, font-family, etc.) on a button-unit,
    // we have to OVERRIDE the existing inline declarations on the button's
    // inner <a>/<span>/<td> nodes.  Without this, an inline `color: #fff` on
    // the inner <a> wins via CSS specificity and the picked color does
    // nothing — exactly the "can't change button text color" symptom the
    // tester reported.
    //
    // Replace-only semantics: we don't ADD new declarations to elements that
    // didn't already have them.  CSS inheritance from the button-unit's own
    // `setStyle` (Style Manager already applied that) covers descendants that
    // lacked the property.  We only need to rewrite the existing inline
    // overrides so they stop winning the cascade.
    const BUTTON_TYPO_PROPS = [
      'color',
      'font-size',
      'font-family',
      'font-weight',
      'font-style',
      'text-decoration',
      'text-align',
      'line-height',
      'letter-spacing',
      'text-transform',
    ] as const;
    const lastButtonTypo = new WeakMap<object, Record<string, string>>();

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    editor.on('component:selected', (comp: any) => {
      if (!comp?.get?.('__buttonUnit')) return;
      const s = (comp.getStyle?.() || {}) as Record<string, string>;
      const snap: Record<string, string> = {};
      for (const k of BUTTON_TYPO_PROPS) {
        const v = (s[k] || '').toString().trim();
        if (v) snap[k] = v;
      }
      lastButtonTypo.set(comp, snap);
    });

    let buttonTypoDebounce: ReturnType<typeof setTimeout> | null = null;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    editor.on('component:styleUpdate', (comp: any) => {
      if (!comp?.get?.('__buttonUnit')) return;
      if (buttonTypoDebounce) clearTimeout(buttonTypoDebounce);
      buttonTypoDebounce = setTimeout(() => {
        buttonTypoDebounce = null;
        try {
          const currStyle = (comp.getStyle?.() || {}) as Record<string, string>;
          const prevSnap = lastButtonTypo.get(comp) || {};
          // Delta-detect: only propagate props that actually changed.  Without
          // this, picking text color would also re-push font-size into every
          // descendant on each event, blowing away per-element overrides the
          // template may have intentionally set.
          const changed: Record<string, string> = {};
          for (const k of BUTTON_TYPO_PROPS) {
            const curr = (currStyle[k] || '').toString().trim();
            const prev = (prevSnap[k] || '').toString().trim();
            if (curr && curr !== prev) changed[k] = curr;
          }
          // Refresh snapshot regardless of whether anything propagates.
          const nextSnap: Record<string, string> = {};
          for (const k of BUTTON_TYPO_PROPS) {
            const v = (currStyle[k] || '').toString().trim();
            if (v) nextSnap[k] = v;
          }
          lastButtonTypo.set(comp, nextSnap);
          if (Object.keys(changed).length === 0) return;

          // Find enclosing mj-text — needed only for content-attribute mode
          // write-back (the bg handler explains the same code path in detail).
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          let mjTextParent: any = comp.parent?.();
          while (mjTextParent && (mjTextParent.get?.('type') || '') !== 'mj-text') {
            mjTextParent = mjTextParent.parent?.();
          }

          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          const um: any = editor.UndoManager;
          const applyTypo = () => {
            // ── Pass 1: walk Backbone subtree, REPLACE existing prop decls ─
            // Same regex pattern as the existing TYPO propagator on mj-text
            // (line ~2888): negative lookbehind prevents `color` from matching
            // inside `background-color`/`border-color`, and `text-decoration`
            // doesn't bite into `text-decoration-color/-line/-style`.
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            const walkComp = (c: any) => {
              if (!c?.get) return;
              const tg = (c.get('tagName') || '').toString().toLowerCase();
              if (tg && tg !== 'br' && tg !== 'hr') {
                try {
                  const cur = (c.getStyle?.() || {}) as Record<string, string>;
                  const next: Record<string, string> = { ...cur };
                  let touched = false;
                  for (const [p, v] of Object.entries(changed)) {
                    // Only override if the descendant ALREADY has this prop.
                    if (cur[p] !== undefined && cur[p] !== v) {
                      next[p] = v;
                      touched = true;
                    }
                  }
                  if (touched) c.setStyle?.(next);
                } catch (_) { /* per-node failure must not break the walk */ }
              }
              c.components?.()?.each?.(walkComp);
            };
            walkComp(comp);

            // ── Pass 2: rewrite live DOM inline `style="…prop:value…"` ─
            // Required because mj-text in content-attribute mode has no
            // Backbone children for the button's descendants — they live as
            // raw HTML inside the content string. setStyle on Backbone won't
            // reach them; only a DOM rewrite will.
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            const btnEl = (comp as any).view?.el as HTMLElement | undefined;
            if (btnEl) {
              const walkDom = (n: HTMLElement) => {
                const styleAttr = n.getAttribute('style') || '';
                if (styleAttr) {
                  let newStyle = styleAttr;
                  let touched = false;
                  for (const [p, v] of Object.entries(changed)) {
                    // Negative lookbehind anchors prop to a property boundary
                    // so `color` ≠ `background-color`/`border-color` etc.
                    const re = new RegExp(`(?<![-a-zA-Z])${p}\\s*:\\s*[^;"']+`, 'gi');
                    if (re.test(newStyle)) {
                      newStyle = newStyle.replace(re, `${p}: ${v}`);
                      touched = true;
                    }
                  }
                  if (touched) n.setAttribute('style', newStyle);
                }
                Array.from(n.children).forEach((cc) => walkDom(cc as HTMLElement));
              };
              walkDom(btnEl);

              // ── Pass 3: serialize back into mj-text.content if needed.
              if (mjTextParent) {
                const contentAttr = (mjTextParent.get?.('content') || '') as string;
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                const pEl = (mjTextParent as any).view?.el as HTMLElement | undefined;
                if (contentAttr && pEl) {
                  mjTextParent.set('content', pEl.innerHTML);
                }
              }
            }
          };

          if (um?.skip) um.skip(applyTypo); else applyTypo();
        } catch (err) {
          console.warn('[GrapesEmailEditor] button-unit typo handler failed:', err);
        }
      }, 30);
    });

    // ── Mirror border / border-radius CSS → MJML attributes ──────────────────
    // grapesjs-mjml renders mj-section/mj-column/etc. from MJML *attributes*,
    // NOT from the component's CSS style object.  When the Style Manager writes
    // `border-radius` or `border-*` properties into comp.style, the next
    // MJML re-render ignores them and the canvas stays unchanged.
    //
    // Fix: listen for style updates on MJML layout components and copy the
    // relevant CSS values into the corresponding MJML attributes so the next
    // compile picks them up.
    //
    // border-radius: GrapesJS Decorations sector stores the shorthand
    //   `border-radius: "70px 0px 0px 0px"` directly.
    // border: stored as individual sub-properties (`border-width`, `border-style`,
    //   `border-color`) — combine them into the MJML `border` shorthand.
    // All MJML component types whose border/border-radius CSS style must be
    // mirrored to MJML attributes.  grapesjs-mjml renders from attributes, not
    // from the CSS style object, so a CSS-only change is ignored on re-render.
    // mj-text is included because the "What Happens Next?" card (and any styled
    // mj-text block) lives here and the user applies border-radius to it.
    const MJML_BORDER_ATTR_TYPES = new Set([
      'mj-section',
      'mj-column',
      'mj-wrapper',
      'mj-hero',
      'mj-text',
      'mj-button',
      'mj-image',
    ]);
    let borderAttrDebounce: ReturnType<typeof setTimeout> | null = null;

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    editor.on('component:styleUpdate', (comp: any) => {
      if (!comp || typeof comp.get !== 'function') return;
      const type = (comp.get('type') || '').toString();
      if (!MJML_BORDER_ATTR_TYPES.has(type)) return;

      if (borderAttrDebounce) clearTimeout(borderAttrDebounce);
      borderAttrDebounce = setTimeout(() => {
        borderAttrDebounce = null;
        try {
          const style = (comp.getStyle?.() || {}) as Record<string, string>;
          const attrs = (comp.getAttributes?.() || {}) as Record<string, string>;

          // ── border-radius ─────────────────────────────────────────────
          // GrapesJS Decorations panel can store the shorthand OR individual
          // corner props (border-top-left-radius etc.) depending on how the
          // user interacts with the four-corner input.  Try shorthand first;
          // reconstruct from individual corners as fallback.
          let brValue = (style['border-radius'] || '').trim();
          if (!brValue) {
            const tl = (style['border-top-left-radius']     || '').trim();
            const tr = (style['border-top-right-radius']    || '').trim();
            const br = (style['border-bottom-right-radius'] || '').trim();
            const bl = (style['border-bottom-left-radius']  || '').trim();
            if (tl || tr || br || bl) {
              brValue = [tl || '0', tr || '0', br || '0', bl || '0'].join(' ');
            }
          }
          if (brValue) {
            comp.addAttributes({ 'border-radius': brValue });
          } else if (attrs['border-radius']) {
            comp.removeAttributes('border-radius');
          }

          // ── border ────────────────────────────────────────────────────
          // Prefer explicit shorthand; fall back to reconstructing from sub-props.
          const bShort  = (style['border']       || '').trim();
          const bWidth  = (style['border-width']  || '').trim();
          const bStyle  = (style['border-style']  || '').trim();
          const bColor  = (style['border-color']  || '').trim();
          const borderValue = bShort || [bWidth, bStyle, bColor].filter(Boolean).join(' ');

          if (borderValue) {
            comp.addAttributes({ border: borderValue });
          } else if (attrs['border']) {
            comp.removeAttributes('border');
          }

          // ── mj-text fallback: write border / border-radius into the
          //    content's outermost wrapping div ──
          // MJML's mj-text does NOT honour `border` or `border-radius`
          // attributes (per the MJML spec — those attrs only exist on
          // mj-section, mj-column, mj-button, mj-image, etc.).  When the
          // admin uses the Decorations panel on the "What Happens Next?"
          // card (an mj-text), the attribute is set but the canvas never
          // changes.  Apply the styles directly to the inner wrapper div
          // (the one preserveCellStyles emits, or any first-level child div
          // already present in the content) so the visual update happens.
          if (type === 'mj-text' && (brValue || borderValue)) {
            const contentAttr: string = (comp.get?.('content') || '') as string;

            // CHILDREN-based mj-text: when content attribute is empty (typical
            // after mjml-code-to-html compile, which moves inner HTML into
            // child model components), find the outermost wrapping child
            // component and update ITS style. Do NOT touch the content
            // attribute — writing there overwrites the rendered structure
            // and the card disappears.
            if (!contentAttr) {
              try {
                const children = comp.components?.();
                if (children && children.length > 0) {
                  // Walk the tree to find the outermost div/table component.
                  // eslint-disable-next-line @typescript-eslint/no-explicit-any
                  const findWrapper = (c: any, depth: number): any => {
                    if (!c || depth > 5) return null;
                    const tag = (c.get?.('tagName') || '').toLowerCase();
                    if (tag === 'div' || tag === 'table') return c;
                    const subs = c.components?.();
                    if (subs && subs.length > 0) {
                      // eslint-disable-next-line @typescript-eslint/no-explicit-any
                      let found: any = null;
                      subs.each((sub: any) => {
                        if (!found) found = findWrapper(sub, depth + 1);
                      });
                      return found;
                    }
                    return null;
                  };
                  // eslint-disable-next-line @typescript-eslint/no-explicit-any
                  let wrapperChild: any = null;
                  children.each((c: any) => {
                    if (!wrapperChild) wrapperChild = findWrapper(c, 0);
                  });
                  if (wrapperChild) {
                    const cur = (wrapperChild.getStyle?.() || {}) as Record<string, string>;
                    const next: Record<string, string> = { ...cur };
                    if (brValue) next['border-radius'] = brValue;
                    if (borderValue) next['border'] = borderValue;
                    if (brValue && /[1-9]/.test(brValue)) next['overflow'] = 'hidden';
                    wrapperChild.setStyle?.(next);
                  }
                }
              } catch (e) {
                console.warn('[GrapesEmailEditor] mj-text children-based border apply failed:', e);
              }
              return; // Skip the content-attr path below
            }

            if (contentAttr && typeof DOMParser !== 'undefined') {
              try {
                const parsed = new DOMParser().parseFromString(
                  `<div data-mjt-root>${contentAttr}</div>`,
                  'text/html',
                );
                const root = parsed.querySelector('[data-mjt-root]');
                if (!root) return;
                // Find the wrapper element that should receive the border.
                // Strategy:
                //  1. If content has a SINGLE element child that's a <div> or
                //     <table>, style it directly (most card-style content).
                //  2. Otherwise (multiple top-level elements, or a heading like
                //     <h3> as first child), inject a new wrapping <div> around
                //     all content.
                const childElements = Array.from(root.children).filter(
                  (n) => (n as HTMLElement).nodeType === 1,
                ) as HTMLElement[];

                let target: HTMLElement | null = null;
                const SINGLE_WRAPPERS = new Set(['div', 'table']);
                if (
                  childElements.length === 1 &&
                  SINGLE_WRAPPERS.has(childElements[0].tagName.toLowerCase())
                ) {
                  target = childElements[0];
                } else if (childElements.length >= 1) {
                  // Inject a wrapping <div> around all top-level nodes.
                  const wrapper = parsed.createElement('div');
                  while (root.firstChild) wrapper.appendChild(root.firstChild);
                  root.appendChild(wrapper);
                  target = wrapper;
                }

                if (target) {
                  let s = target.getAttribute('style') || '';
                  const setProp = (prop: string, val: string) => {
                    const re = new RegExp(`(?<![-a-zA-Z])${prop}\\s*:\\s*[^;"']+;?\\s*`, 'gi');
                    s = s.replace(re, '');
                    s = s.replace(/;?\s*$/, '');
                    s += `; ${prop}: ${val};`;
                  };
                  if (brValue) setProp('border-radius', brValue);
                  if (borderValue) setProp('border', borderValue);
                  // Cards with rounded corners also need overflow:hidden so
                  // children don't poke through the radius. Only add it when
                  // border-radius is non-zero.
                  if (brValue && /[1-9]/.test(brValue)) setProp('overflow', 'hidden');
                  target.setAttribute('style', s.replace(/^[\s;]+/, ''));
                  const updated = root.innerHTML;
                  if (updated !== contentAttr) {
                    comp.set('content', updated);
                  }
                }
              } catch (e) {
                console.warn('[GrapesEmailEditor] mj-text border DOM apply failed:', e);
              }
            }
          }
        } catch (err) {
          console.warn('[GrapesEmailEditor] border attr mirror failed:', err);
        }
      }, 30);
    });

    // ── Unified safe-compile command ──
    // Any caller OUTSIDE this component (handleSave, getLatestHtml) that
    // needs fresh HTML must go through this command instead of calling
    // `mjml-code-to-html` directly. The raw MJML compile rerenders mj-text
    // internals — fresh DOM nodes arrive without our selectable:false flags
    // or widened stylable list. Without re-applying those, the admin's next
    // click after a Preview or Save lands on a raw <td> and loses the RTE
    // toolbar / Decorations sector. This command wraps the compile with the
    // same post-processing extractAndNotify does, so every code path that
    // triggers a compile leaves the editor in a consistent state.
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    (editor as any).Commands.add('mjml-compile-safe', {
      run: (ed: Editor) => {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const result = ed.runCommand('mjml-code-to-html') as any;
        try {
          markMjTextDescendants(ed);
          expandStylableAcrossTree(ed);
        } catch (err) {
          console.warn('[GrapesEmailEditor] mjml-compile-safe post-processing failed:', err);
        }
        return result;
      },
    });

    // NOTE: cleanupStyleManager() is called in onReady, NOT here.

    // ── RTE state tracking ──
    // When admin is editing text (RTE active), skip MJML compilation on
    // component:update — compilation can reset the canvas and jump the cursor.
    // Compile once when the admin finishes editing (rte:disable).
    let rteActive = false;

    // Track the last mousedown coordinates in the canvas iframe.
    // Used by the component:selected safety net: when an inner selectable element
    // intercepts a click, the safety net re-selects the mj-text.  GrapesJS then
    // calls el.focus() (resetting cursor to 0) before firing rte:enable.  We save
    let lastCanvasClickX = -1;
    let lastCanvasClickY = -1;
    // Pending cursor restore: set by safety net setTimeout, consumed by rte:enable.
    let pendingCursorRestoreX = -1;
    let pendingCursorRestoreY = -1;
    try {
      const canvasDoc = editor.Canvas.getDocument();
      if (canvasDoc) {
        canvasDoc.addEventListener('mousedown', (e: MouseEvent) => {
          lastCanvasClickX = e.clientX;
          lastCanvasClickY = e.clientY;
        });
      }
    } catch (_) {}

    editor.on('rte:enable', () => {
      rteActive = true;
      rteActiveRef.current = true;
      // Reset sticky inline-bg selection from any prior RTE session so a
      // stale selection from a different block never blocks the whole-block
      // background-color path on the new component.
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      (editor as any).__rteTextSelection = '';
      // Save the component being edited so variable drag-drop can insert inline.
      const selComp = editor.getSelected();
      // Pen colour belongs to one text block; editing another block ends it.
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      if (selComp?.cid !== (editor as any).__penBlockCid) {
        clearPen(editor);
      }
      // While editing, a mouse-drag means "select text", not "move this block".
      if (selComp && selComp.get('draggable') !== false) {
        // Keep the original value: for mj-text it is a rule ("only into a column"), not just true.
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        (editor as any).__rteDragPrev = selComp.get('draggable');
        selComp.set({ draggable: false }, { silent: true });
        selComp.view?.el?.setAttribute?.('draggable', 'false');
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        (editor as any).__rteDragLocked = selComp;
      }
      if (selComp) {
        (editor as any).__lastRteComponent = selComp;
        // Dedicated drag-var state — NOT cleared by rte:disable or component:selected.
        // Persists after rte:disable so block:drag:start (which fires AFTER rte:disable)
        // can still find the target component and cursor offset.
        // Reset here (new edit session) so stale state from a different component
        // is never used.
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        (editor as any).__dragVarComp   = selComp;
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        (editor as any).__dragVarOffset = -1; // trackSel will update this
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        (editor as any).__dragVarPath = null;
      }
      // Track cursor position for the Insert Variable dropdown.
      // Clicking the <select> moves focus out of contenteditable, losing
      // the caret.  Save the last known range so the dropdown handler
      // can restore it before inserting.
      const doc = editor.Canvas.getDocument();
      if (doc) {
        const trackSel = () => {
          try {
            const sel = doc.getSelection?.();
            const rangeCount = sel?.rangeCount || 0;
            if (sel && rangeCount > 0) {
              const r = sel.getRangeAt(0);  
              // Save DOM Range (toolbar actions use this to restore selection).
              // eslint-disable-next-line @typescript-eslint/no-explicit-any
              (editor as any).__lastRteCaret = r.cloneRange();
              // Save text offset into __dragVarOffset (persists past rte:disable).
              // eslint-disable-next-line @typescript-eslint/no-explicit-any
              const dragComp = (editor as any).__dragVarComp;
              const compEl   = (dragComp as any)?.view?.el as HTMLElement | undefined;
              if (compEl) {
                // On an empty line the caret sits in an element, not a text node; count it too.
                const off = getTextOffset(compEl, r.startContainer, r.startOffset);
                const char_off = off >= 0 ? off : getCharOffset(compEl, r.startContainer, r.startOffset);
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                if (char_off >= 0) (editor as any).__dragVarOffset = char_off;
                // Exact spot (tells "end of line 1" from "start of line 2", which share an offset).
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                (editor as any).__dragVarPath = getCaretPath(compEl, r.startContainer, r.startOffset);
              }
              // Save the live range for toolbar actions (link / colour /
              // insert).  Save COLLAPSED ranges too — the link button needs a
              // valid cursor position when the user just clicked into the text
              // without selecting anything, otherwise the link gets inserted
              // eslint-disable-next-line @typescript-eslint/no-explicit-any
              (editor as any).__lastRteRange = r.cloneRange();
              syncTextColorSwatch(editor, doc);
              if (!r.collapsed) {
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                (editor as any).__lastRteRangeText = r.toString();
                // Sticky version: persists through rte:disable and inline applies
                // so the background-color guard keeps working when the user drags
                // the color picker after the first inline span is applied.
                // Cleared only by component:deselected or a new rte:enable.
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                (editor as any).__rteTextSelection = r.toString();
              }
            }
          } catch (_) { /* ignore */ }
        };
        doc.addEventListener('selectionchange', trackSel);
        doc.addEventListener('keyup', trackSel);
        doc.addEventListener('mouseup', trackSel);
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        (editor as any).__trackSelCleanup = () => {
          doc.removeEventListener('selectionchange', trackSel);
          doc.removeEventListener('keyup', trackSel);
          doc.removeEventListener('mouseup', trackSel);
        };
      }

      // Restore cursor to the click position after rte:enable resets it via focus().
      // GrapesJS's RichTextEditorModule.enable() is async — it awaits
      // customRte.enable() (which calls el.focus()) BEFORE firing rte:enable.
      // So by the time our handler runs, focus() has already reset caret to 0.
      // requestAnimationFrame runs after the current call stack clears and
      // caretRangeFromPoint maps pixel coordinates → correct text offset.
      //
      // Use pendingCursorRestoreX/Y (set by safety net) if available; otherwise
      // fall back to lastCanvasClickX/Y (covers any re-activation scenario).
      // A bounds check prevents misplacement when rte:enable fires programmatically
      // (no recent canvas click — e.g., drag-drop, undo).
      {
        const rx = pendingCursorRestoreX >= 0 ? pendingCursorRestoreX : lastCanvasClickX;
        const ry = pendingCursorRestoreY >= 0 ? pendingCursorRestoreY : lastCanvasClickY;
        pendingCursorRestoreX = -1;
        pendingCursorRestoreY = -1;
        if (rx >= 0 && ry >= 0) {
          requestAnimationFrame(() => {
            try {
              const cd = editor.Canvas.getDocument();
              if (!cd) return;
              // Bounds check: skip if click was outside this component's DOM element
              // (e.g. toolbar click, drag-drop, programmatic activation).
              const selComp = editor.getSelected();
              // eslint-disable-next-line @typescript-eslint/no-explicit-any
              const rteEl = (selComp as any)?.view?.el as HTMLElement | undefined;
              if (rteEl) {
                const rect = rteEl.getBoundingClientRect();
                if (rx < rect.left || rx > rect.right || ry < rect.top || ry > rect.bottom) return;
              }
              // eslint-disable-next-line @typescript-eslint/no-explicit-any
              const range = (cd as any).caretRangeFromPoint?.(rx, ry)
                // Firefox: caretPositionFromPoint
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                ?? (() => { const p = (cd as any).caretPositionFromPoint?.(rx, ry); if (!p) return null; const r2 = cd.createRange(); r2.setStart(p.offsetNode, p.offset); r2.collapse(true); return r2; })();
              if (range) {
                const sel = cd.getSelection?.();
                if (sel) { sel.removeAllRanges(); sel.addRange(range); }
              }
            } catch (_) {}
          });
        }
      }
    });

    editor.on('rte:disable', () => {
      rteActive = false;
      rteActiveRef.current = false;
      // Editing ended — the block can be dragged again.
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const dragLocked = (editor as any).__rteDragLocked;
      if (dragLocked) {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const prev = (editor as any).__rteDragPrev;
        dragLocked.set({ draggable: prev === undefined ? true : prev }, { silent: true });
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        (editor as any).__rteDragPrev = undefined;
        dragLocked.view?.el?.setAttribute?.('draggable', 'true');
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        (editor as any).__rteDragLocked = null;
      }
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      (editor as any).__lastRteCaret = null;
      // NOTE: __dragVarComp and __dragVarOffset are intentionally NOT cleared here.
      // rte:disable fires BEFORE block:drag:start when user clicks the block panel,
      // so clearing them here would wipe the target before the drag handler reads it.
      // They are only reset in rte:enable (new edit session on a different component).
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      (editor as any).__trackSelCleanup?.();
      // Compile now that text editing is done.
      // Pause UndoManager so the extraction doesn't push internal artifacts.
      // Optional chaining: rte:disable can fire during component teardown
      // (user clicks Back while RTE is focused — blur triggers this handler
      // mid-unmount). UndoManager may already be gone.
      //
      // CRITICAL try/finally: same fix as debouncedExtract — if
      // extractAndNotify throws, UndoManager MUST be restarted, else the
      // user's Undo button does nothing for the rest of the session.
      let umStopped = false;
      try {
        try { editor.UndoManager?.stop?.(); umStopped = true; } catch { /* UM unavailable */ }
        try { if (isLoadedRef.current) extractAndNotify(editor); }
        finally {
          if (umStopped) {
            try { editor.UndoManager?.start?.(); } catch { /* never leave UM stopped */ }
          }
        }
      } catch (err) {
        console.warn('[GrapesEmailEditor] rte:disable handler skipped — editor may be unmounting:', err);
      }
    });

    // Clear saved RTE component on any selection change so a stale component
    // from a previous edit session is never mistakenly used as the inline
    // insert target.  Re-set immediately if user enters RTE (rte:enable above).
    editor.on('component:selected', () => {
      (editor as any).__lastRteComponent = null;
    });

    // Clear the sticky inline-bg selection when the user navigates away from
    // an mj-text so the background-color guard doesn't permanently block
    // whole-block background changes on unrelated components.
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    editor.on('component:deselected', (comp: any) => {
      if (!comp || typeof comp.get !== 'function') return;
      if ((comp.get('type') || '').toString() === 'mj-text') {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        (editor as any).__rteTextSelection = '';
      }
    });

    // ── Safety net: redirect raw-HTML selections inside mj-text → mj-text ──
    //
    // WHY: After mjml-code-to-html re-renders mj-text internals, GrapesJS can
    // create new Backbone models for the compiled <td>/<tr>/<table> nodes. In
    // some cases their Backbone parent chain no longer reaches the mj-text
    // model (they are re-parented under the MJML-compiled wrapper <td>), so
    // markMjTextDescendants misses them and they stay selectable. This handler
    // fires on every component:selected; if the selected component is a raw
    // non-MJML node that sits INSIDE an mj-text's DOM element, it immediately:
    //   1. Silences the node (selectable:false) to prevent repeat clicks.
    //   2. Re-selects the containing mj-text so the RTE toolbar activates.
    //
    // Two-phase lookup (fast path + DOM-containment fallback) so it works
    // regardless of whether the Backbone parent chain is intact.
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    editor.on('component:selected', (comp: any) => {
      if (!comp || typeof comp.get !== 'function') return;
      const type = (comp.get('type') || '').toString();
      // Skip mj-* top-level components — they are always intentionally selectable.
      if (type.startsWith('mj-')) return;
      // Skip button-units — they are intentionally selectable so the user can
      // recolor the button independently from the surrounding mj-text.
      if (comp.get('__buttonUnit')) return;
      // Skip trait-editable descendants (<a>, <img>) — they need their own selection.
      if (isTraitEditableDescendant(comp)) return;

      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      let mjTextTarget: any = null;

      // Phase 1 — fast Backbone parent-chain walk
      let anc = comp.parent?.();
      while (anc) {
        if ((anc.get('type') || '') === 'mj-text') { mjTextTarget = anc; break; }
        anc = anc.parent?.();
      }

      // Phase 2 — DOM containment (handles post-compile re-parented nodes)
      if (!mjTextTarget) {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const compEl = (comp as any).view?.el as HTMLElement | undefined;
        if (compEl?.isConnected) {
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          const findInTree = (c: any): any => {
            if ((c.get('type') || '') === 'mj-text') {
              // eslint-disable-next-line @typescript-eslint/no-explicit-any
              const el = (c as any).view?.el as HTMLElement | undefined;
              if (el && el !== compEl && el.contains(compEl)) return c;
            }
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            let hit: any = null;
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            c.components?.()?.each?.((child: any) => { if (!hit) hit = findInTree(child); });
            return hit;
          };
          const wrapperComp = editor.DomComponents?.getWrapper?.();
          if (wrapperComp) mjTextTarget = findInTree(wrapperComp);
        }
      }

      if (!mjTextTarget) return;

      // Silence this node permanently so future clicks skip directly to mj-text.
      comp.set(
        { selectable: false, hoverable: false, highlightable: false, layerable: false, editable: false },
        { silent: true },
      );
      // Defer the re-select to avoid re-entrancy with GrapesJS's selection cycle.
      // Capture coordinates NOW (before the timeout) so they're stable.
      const clickX = lastCanvasClickX;
      const clickY = lastCanvasClickY;
      setTimeout(() => {
        try {
          // Signal rte:enable to restore the cursor after focus() resets it.
          // Must be set BEFORE editor.select() so rte:enable can read it.
          if (clickX >= 0 && clickY >= 0) {
            pendingCursorRestoreX = clickX;
            pendingCursorRestoreY = clickY;
          }
          editor.select(mjTextTarget);
        } catch (_) {}
      }, 0);
    });

    // ── Section-level drop precision correction ──────────────────────────────
    // mj-body's getChildrenSelector → 'div' resolves to the FIRST mj-section
    // element after render (not the wrapper), so the GrapesJS sorter sees only
    // one container child → 2 drop indicators at extreme top/bottom.
    // We cannot safely override mj-body's getChildrenSelector (causes blank
    // canvas when null is returned from querySelector with no null-safety in
    // the GrapesJS base getChildrenContainer).
    //
    // Strategy: post-drop reposition.
    //   1. During drag, track pointer Y from pointermove on the outer <iframe>
    //      element (NOT canvas window mousemove — pointer events are suspended
    //      inside the canvas iframe during GrapesJS block drag).
    //   2. On block:drag:stop, if a new mj-section landed at wrong index:
    //      convert outer clientY → iframe viewport Y (subtract frameEl.top),
    //      compare against section midpoints, move to correct index. ✓
    let lastOuterPointerY = -1;
    const onSectionPointerMove = (e: PointerEvent) => { lastOuterPointerY = e.clientY; };

    editor.on('block:drag:start', () => {
      lastOuterPointerY = -1;
      try {
        const frameEl = editor.Canvas.getFrameEl();
        if (frameEl) {
          frameEl.removeEventListener('pointermove', onSectionPointerMove as EventListener);
          frameEl.addEventListener('pointermove', onSectionPointerMove as EventListener);
        }
      } catch (_) {}
    });

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    editor.on('block:drag:stop', (droppedComp: any) => {
      // Always clean up the pointer listener regardless of what was dropped.
      try {
        const frameEl = editor.Canvas.getFrameEl();
        if (frameEl) frameEl.removeEventListener('pointermove', onSectionPointerMove as EventListener);
      } catch (_) {}

      if (!droppedComp) return;
      const droppedType = (droppedComp.get?.('type') || '').toString();
      if (droppedType !== 'mj-section') return;

      const outerY = lastOuterPointerY;
      if (outerY < 0) return;

      // Convert outer-document clientY → iframe viewport Y.
      // frameEl.getBoundingClientRect().top = outer-viewport Y of the iframe's
      // top edge.  Subtracting it gives the position relative to the iframe's
      // visible area, which matches getBoundingClientRect() inside the iframe.
      let mouseY: number;
      try {
        const frameRect = editor.Canvas.getFrameEl()?.getBoundingClientRect();
        if (!frameRect) return;
        mouseY = outerY - frameRect.top;
      } catch (_) { return; }

      const parent = droppedComp.parent?.();
      if (!parent) return;

      const siblings = parent.components();
      const N: number = siblings.length;

      // Walk sibling list: record dropped index and collect mid-Y of other sections.
      let droppedIdx = -1;
      const otherSections: Array<{ origIdx: number; midY: number }> = [];

      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      siblings.each((child: any, idx: number) => {
        if (child === droppedComp) { droppedIdx = idx; return; }
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const el = (child as any).view?.el as HTMLElement | undefined;
        if (!el || !el.isConnected) return;
        const rect = el.getBoundingClientRect();
        if (rect.height === 0) return;
        otherSections.push({ origIdx: idx, midY: (rect.top + rect.bottom) / 2 });
      });

      if (droppedIdx < 0) return;

      // Determine targetIdx: insert BEFORE the first sibling whose midY exceeds mouseY.
      // If no such sibling exists, insert after all (targetIdx = N).
      let targetIdx = N;
      for (const s of otherSections) {
        if (mouseY < s.midY) { targetIdx = s.origIdx; break; }
      }

      // Effective final index after remove+insert = min(targetIdx, N-1).
      if (Math.min(targetIdx, N - 1) === droppedIdx) return; // already in correct position

      console.warn('[SECTION-DROP] correcting: idx %d → %d (mouseY=%d)', droppedIdx, targetIdx, mouseY);

      // Defer so GrapesJS finishes its own drop-handling cycle first.
      setTimeout(() => {
        try {
          const par = droppedComp.parent?.();
          if (!par) return;
          const col = par.components();
          col.remove(droppedComp);
          col.add(droppedComp, { at: targetIdx });
          editor.select(droppedComp);
        } catch (err) {
          console.warn('[GrapesEmailEditor] Section drop correction failed:', err);
        }
      }, 0);
    });

    // ── Variable inline drag-drop ──
    // GrapesJS 0.22: block:drag:stop(droppedComponent, block).
    //
    // Strategy: TEXT OFFSET (character count from start of mj-text's view el).
    //
    // Why not DOM Range: rte:disable → MJML compile re-renders all mj-text child
    // nodes.  Any saved Range.startContainer becomes a detached/dead node.
    // contains() returns false → insertion always fell back to end-of-paragraph.
    //
    // Text offsets are stable: the visible text doesn't change during MJML
    // compilation, only the DOM node references do.  getNodeAtTextOffset walks
    // the CURRENT (post-render) text nodes to find the right insertion point.
    //
    // Flow:
    //   trackSel        → saves __lastRteCaretTextOffset whenever cursor moves
    //   block:drag:start → snapshots offset + target component
    //   block:drag:stop  → getNodeAtTextOffset → createRange → insertNode
    //
    // SNAPSHOT: pendingVarTarget captured at drag:start because component:selected
    // fires during hover and clears __lastRteComponent mid-drag.
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    let pendingVarTarget: any = null;
    let pendingVarTextOffset  = -1;
    let pendingVarPath: number[] | null = null;

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    // Tracks whether UndoManager was stopped for this var-block drag so the
    // stop handler knows to restart it and collapse the drop into one undo step.
    let varDragUndoStopped = false;
    // Safety net: if block:drag:stop never fires (browser quirk, drag aborted
    // by page nav, etc.) UndoManager would stay permanently stopped and the
    // user's Undo button would silently do nothing for the rest of the
    // session.  This timeout restarts it after 10 s as a fail-safe; the real
    // drag:stop handler clears the timer first thing if it fires normally.
    let varDragUmFallbackTimer: ReturnType<typeof setTimeout> | null = null;

    editor.on('block:drag:start', (block: any) => {
      pendingVarTarget     = null;
      pendingVarTextOffset = -1;
      const blockId: string = block?.getId?.() || '';
      if (!blockId.startsWith('var-')) return;
      // Use __dragVarComp (NOT __lastRteComponent).  rte:disable fires before
      // block:drag:start when the user clicks the block panel, so __lastRteComponent
      // is already null by now.  __dragVarComp persists past rte:disable.
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const savedComp = (editor as any).__dragVarComp;
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const savedOff  = (editor as any).__dragVarOffset as number | undefined;
      console.warn('[VAR-DROP] drag:start — comp:', !!savedComp, 'offset:', savedOff);
      if (!savedComp) return;
      pendingVarTarget     = savedComp;
      pendingVarTextOffset = (typeof savedOff === 'number' && savedOff >= 0) ? savedOff : -1;
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      pendingVarPath       = (editor as any).__dragVarPath ?? null;
      // Stop UndoManager before the block lands so GrapesJS's own "add component"
      // action is NOT recorded.  block:drag:stop will restart it after removing
      // the dropped shell, leaving only the content-write as the single undo step.
      editor.UndoManager?.stop?.();
      varDragUndoStopped = true;
      // Safety net: ensure UM gets restarted even if drag:stop never fires.
      if (varDragUmFallbackTimer) clearTimeout(varDragUmFallbackTimer);
      varDragUmFallbackTimer = setTimeout(() => {
        varDragUmFallbackTimer = null;
        if (varDragUndoStopped) {
          varDragUndoStopped = false;
          try { editor.UndoManager?.start?.(); } catch { /* ignore */ }
          console.warn('[GrapesEmailEditor] var-drag UM auto-restarted by fallback timer (drag:stop never fired)');
        }
      }, 10000);
    });

    // GrapesJS 0.22 signature: block:drag:stop(droppedComponent, block)
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    editor.on('block:drag:stop', (droppedComp: any, block: any) => {
      // Drag:stop fired normally — cancel the UM fail-safe timer.
      if (varDragUmFallbackTimer) {
        clearTimeout(varDragUmFallbackTimer);
        varDragUmFallbackTimer = null;
      }
      const target     = pendingVarTarget;
      const textOffset = pendingVarTextOffset;
      const varPath    = pendingVarPath;
      pendingVarTarget     = null;
      pendingVarTextOffset = -1;
      pendingVarPath       = null;
      // Read and reset the stopped flag immediately so it can't leak to the
      // next drag if this one exits early.
      const wasStopped = varDragUndoStopped;
      varDragUndoStopped = false;

      const blockId: string = block?.getId?.() || '';
      if (!blockId.startsWith('var-') || !droppedComp || !target) {
        // Cancelled drag or no target — restart UndoManager if we stopped it.
        if (wasStopped) editor.UndoManager?.start?.();
        return;
      }
      const varName  = blockId.replace(/^var-/, '');
      const varToken = `{{${varName}}}`;

      // Capture finalHtml and write-path SYNCHRONOUSLY (DOM insert while UM is
      // still stopped).  The actual model write is deferred into setTimeout(0)
      // so it runs AFTER droppedComp.remove() — making it the ONE undo entry.
      let finalHtml: string | null = null;
      let usesContentAttr = false;

      try {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const insertEl  = (target as any).view?.el as HTMLElement | undefined;
        const canvasDoc = editor.Canvas.getDocument();
        let inserted    = false;
        console.warn('[VAR-DROP] drag:stop — insertEl:', !!insertEl, 'textOffset:', textOffset, 'connected:', insertEl?.isConnected);

        // ── PRIMARY: text-offset insertion ──────────────────────────────
        // getNodeAtTextOffset walks the current (post-MJML-compile) text nodes
        // to find the correct insertion point even after DOM re-render.
        if (insertEl && insertEl.isConnected && canvasDoc && textOffset >= 0) {
          const pos = getInsertPosition(insertEl, varPath, textOffset);
          console.warn('[VAR-DROP] pos:', pos?.container?.nodeType, 'offset:', pos?.offset);
          if (pos) {
            const textNode = canvasDoc.createTextNode(varToken);
            const r = canvasDoc.createRange();
            r.setStart(pos.container, pos.offset);
            r.collapse(true);
            r.insertNode(textNode);
            finalHtml = insertEl.innerHTML;
            const cAttr: string = (target.get?.('content') || '') as string;
            usesContentAttr = !!cAttr;
            // Advance the stored offset past the inserted token so a second
            // consecutive drag inserts AFTER this variable, not before it.
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            (editor as any).__dragVarOffset = textOffset + varToken.length;
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            (editor as any).__dragVarPath = null;
            inserted = true;
          }
        }

        // ── FALLBACK: no cursor position — insert before last block tag ──
        if (!inserted) {
          const contentAttr: string = (target.get?.('content') || '') as string;
          usesContentAttr = !!contentAttr;
          const source = contentAttr || insertEl?.innerHTML || '';
          let newHtml: string;
          const lastP   = source.lastIndexOf('</p>');
          const lastDiv = source.lastIndexOf('</div>');
          const lastTd  = source.lastIndexOf('</td>');
          if (lastP   !== -1) { newHtml = source.slice(0, lastP)   + varToken + source.slice(lastP); }
          else if (lastDiv !== -1) { newHtml = source.slice(0, lastDiv) + varToken + source.slice(lastDiv); }
          else if (lastTd  !== -1) { newHtml = source.slice(0, lastTd)  + varToken + source.slice(lastTd); }
          else                     { newHtml = source + ' ' + varToken; }
          finalHtml = newHtml;
        }

        // Defer removal so GrapesJS finishes its own drop handling first.
        // Sequence while UndoManager may still be stopped (wasStopped):
        //   1. droppedComp.remove()  → NOT recorded (UM stopped)
        //   2. UndoManager.start()   → resume
        //   3. target.set('content') → ONE atomic undo step
        setTimeout(() => {
          try { droppedComp.remove(); } catch (_) {}
          if (wasStopped) editor.UndoManager?.start?.();
          if (finalHtml !== null) {
            try {
              if (usesContentAttr) { target.set('content', finalHtml); }
              // eslint-disable-next-line @typescript-eslint/no-explicit-any
              else { (target as any).components?.(finalHtml); }
            } catch (e) {
              console.warn('[GrapesEmailEditor] Variable content write failed:', e);
            }
          }
        }, 0);
      } catch (err) {
        // Safety: always restart UndoManager — a permanent stop would break all
        // subsequent editing actions.
        if (wasStopped) editor.UndoManager?.start?.();
        console.warn('[GrapesEmailEditor] Variable inline insert failed:', err);
      }
    });

    // ── Catch dynamically added descendants of mj-text ──
    // Primary gating happens via markMjTextDescendants() in onReady (direct
    // tree walk after load).  This listener covers the live-editing case:
    // admin drops a new block inside mj-text, or RTE splits a paragraph into
    editor.on('component:add', (component: any) => {
      // Stylable widening for Decorations — the admin just dropped a new
      // block; walk the new subtree once so every component in it has the
      // expanded stylable list. Silent internally, so no extract bounce.
      const walkForStylable = (c: any) => {
        expandStylableOnComponent(c);
        c?.components?.()?.each?.(walkForStylable);
      };
      walkForStylable(component);

      // ── mj-text descendant handling for live-add (drops + loadProjectData) ──
      // Walk ancestors looking for mj-text.  Inside one, decide between:
      //   (a) promote to button-unit (styled <a>/<button>/single-cell button table)
      //   (b) leave selectable (trait-editable: bare <a>, <img>)
      //   (c) silence (default for other raw-HTML descendants of mj-text)
      //
      // Critically, the button-unit check must run BEFORE the trait-editable
      // early-return — otherwise a button-style <a> falls through case (b) and
      // never gets the __buttonUnit flag in the live-add path.  Without this
      // ordering, undo/redo (which re-fires component:add for every restored
      // component) would lose Button promotion on every history step.
      let ancestor = component.parent?.();
      while (ancestor) {
        if (ancestor.get('type') === 'mj-text') {
          if (isButtonUnitWrapper(component)) {
            component.set(
              {
                selectable: true,
                hoverable: true,
                highlightable: true,
                layerable: true,
                editable: false,
                name: 'Button',
                __buttonUnit: true,
              },
              { silent: true },
            );
            component.components?.()?.each?.(silenceSubtree);
            return;
          }
          if (isTraitEditableDescendant(component)) return;
          component.set({
            selectable: false,
            hoverable: false,
            highlightable: false,
            layerable: false,
            editable: false,
          });
          return;
        }
        ancestor = ancestor.parent?.();
      }
    });

    // ── Sidebar → text-selection router (word-level inline styling) ──
    //
    // Admin expects: "I selected 'Hello' → changed font-size/color/family in
    // the right-side Style Manager → ONLY 'Hello' changes, not the whole
    // mj-text block."
    //
    // TRIGGER: component:styleUpdate (fires AFTER style commit, so
    // comp.getStyle() returns the canonical new value). Using style:
    // property:update here is unreliable — its event argument shows
    // property/value = undefined for many GrapesJS property types.
    //
    // DIFF DETECTION: we can't trust `prop.get('property')` either, so we
    // snapshot the component's text-level style on component:selected and
    // compute the changed key(s) by comparing current vs snapshot at each
    // styleUpdate. Only one key typically changes per sidebar click.
    //
    // SELECTION SIGNAL: `__lastRteRange` (maintained on every
    // selectionchange/keyup/mouseup inside the canvas iframe, see
    // rte:enable handler above). If it exists and is non-collapsed, the
    // admin had a text selection before clicking the sidebar — we restore
    // and wrap it.
    //
    // REVERT: after wrapping selection in a <span> with the changed inline
    // style, remove that property from the component's own style so it
    // doesn't also apply to the whole block. `applyingInline` guard stops
    // the removeStyle from re-entering this handler.
    const TEXT_LEVEL_PROPS = [
      'font-size',
      'font-weight',
      'color',
      'font-family',
      'font-style',
      'text-decoration',
      'line-height',
      'letter-spacing',
      'text-transform',
      // background-color must be here so selecting text then picking a bg color
      // wraps only the selection in a <span style="background-color:…"> instead
      // of applying container-background-color to the entire mj-text block.
      'background-color',
    ] as const;
    const lastTextStyle = new WeakMap<object, Record<string, string>>();
    let applyingInline = false;

    // Seed snapshot on selection so first styleUpdate has a baseline.
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    editor.on('component:selected', (comp: any) => {
      if (!comp || typeof comp.get !== 'function') return;
      const type = (comp.get('type') || '').toString();
      if (type !== 'mj-text') return;
      const snap: Record<string, string> = {};
      const s = comp.getStyle?.() || {};
      for (const k of TEXT_LEVEL_PROPS) {
        if (s[k] !== undefined) snap[k] = s[k] as string;
      }
      lastTextStyle.set(comp, snap);
    });

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    editor.on('component:styleUpdate', (comp: any) => {
      try {
        if (applyingInline) return;
        if (!comp || typeof comp.get !== 'function') return;
        const type = (comp.get('type') || '').toString();
        if (type !== 'mj-text') return;
        // Prefer the sticky selection (persists through rte:disable and after
        // the first inline apply) so repeated color-picker drags still work.
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const savedText = (
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          ((editor as any).__rteTextSelection as string | undefined) ||
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          ((editor as any).__lastRteRangeText as string | undefined)
        );
        if (!savedText || !savedText.trim()) return;

        const currentStyle = comp.getStyle?.() || {};
        const prevSnap = lastTextStyle.get(comp) || {};

        // Find which text-level property changed.
        let changedProp: string | null = null;
        let changedValue = '';
        for (const k of TEXT_LEVEL_PROPS) {
          const curr = (currentStyle[k] || '') as string;
          const prev = (prevSnap[k] || '') as string;
          if (curr !== prev && curr) {
            changedProp = k;
            changedValue = curr;
            break;
          }
        }
        const nextSnap: Record<string, string> = {};
        for (const k of TEXT_LEVEL_PROPS) {
          if (currentStyle[k] !== undefined) nextSnap[k] = currentStyle[k] as string;
        }
        lastTextStyle.set(comp, nextSnap);

        if (!changedProp || !changedValue) return;

        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const mjText = comp as any;
        const view = mjText.view;
        const el = view?.el as HTMLElement | undefined;
        const contentAttr = mjText.get?.('content') || '';
        const innerHtml = el?.innerHTML || '';

        // Prefer inner HTML from the live DOM (what the admin actually sees).
        // Fall back to content attribute if DOM not available.
        const content: string = innerHtml || contentAttr;
        if (typeof content !== 'string' || !content) return;

        // Escape regex metachars so `savedText` is matched literally.
        // Then make whitespace flexible — HTML content can have text nodes
        // broken across tags (e.g. "Great <strong>news</strong>"), or use
        // `&nbsp;` entities for non-breaking spaces, neither of which a
        // strict space-char regex would match.
        const escapedLiteral = savedText.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
        // Any run of whitespace in savedText → match whitespace OR entity
        // OR even inline tags (limited to short stretches).
        const flexiblePattern = escapedLiteral
          .split(/\s+/)
          .filter(Boolean)
          .join('(?:\\s|&nbsp;|<[^>]+>){1,20}');

        const reLiteral = new RegExp(escapedLiteral);
        const reFlexible = new RegExp(flexiblePattern);

        let re: RegExp | null = null;
        if (reLiteral.test(content)) {
          re = reLiteral;
        } else if (reFlexible.test(content)) {
          re = reFlexible;
        }
        if (!re) {
          return;
        }

        const styleDecl = `${changedProp}: ${changedValue}`;
        // Use $& (entire match) so flexible-regex hits preserve any tags
        // that lie between words (e.g. "Great <strong>news</strong>").
        const wrapped = `<span style="${styleDecl}">$&</span>`;

        // Strip any existing <span> around this exact text that already carries
        // changedProp.  Prevents span nesting on repeated color-picker drags:
        //   first drag:  "word" → <span style="background-color:red">word</span>
        //   second drag: strip red span → "word" → re-wrap with new color.
        const safeText4Strip = savedText.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
        const stripRe = new RegExp(`<span([^>]*)>(${safeText4Strip})<\\/span>`, 'gi');
        const strippedContent = content.replace(
          stripRe,
          (_m: string, attrs: string, inner: string) => {
            const sm = attrs.match(/\sstyle="([^"]*)"/i);
            if (!sm) return _m;
            const hasProp = new RegExp(
              `(?:^|;)\\s*${changedProp.replace(/-/g, '[-]')}\\s*:`,
              'i',
            ).test(sm[1]);
            if (!hasProp) return _m;
            const cleaned = sm[1]
              .split(';')
              .map((s: string) => s.trim())
              .filter((s: string) => s && !s.toLowerCase().startsWith(changedProp))
              .join('; ');
            if (!cleaned) return inner;
            return `<span${attrs.replace(sm[0], ` style="${cleaned}"`)}>${inner}</span>`;
          },
        );
        const updated = strippedContent.replace(re, wrapped);

        applyingInline = true;
        try {
          // Two write paths depending on where the content originally lived:
          //   - If the plugin stored it in the `content` attribute, keep
          //     setting via set('content', ...) — mj-text's view re-renders
          //     from that attribute on change.
          //   - If the text lived as child components / live DOM, we use
          //     mjText.components(html) which parses and replaces children.
          if (contentAttr) {
            mjText.set('content', updated);
          } else {
            // Replace structured children with the new HTML.
            mjText.components(updated);
          }
          // Revert the component-level style so the whole block doesn't
          // also carry this prop.
          mjText.removeStyle?.(changedProp);
        } finally {
          applyingInline = false;
        }

        // Clear saved text — the selection is now a span with the style.
        // Admin must reselect to apply another style (prevents accidental
        // re-wrapping the same text on a later styleUpdate).
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        (editor as any).__lastRteRangeText = '';
      } catch (err) {
        console.warn('[GrapesEmailEditor] sidebar→selection router failed:', err);
      }
    });


    // ── Block-level typography propagation into mj-text descendants ──
    //
    // WHY: legacy templates nest <div>/<table>/<span> inside mj-text with
    // inline color/font-size/font-family (from htmlToMjml.preserveCellStyles
    // and pasted HTML). Those inline rules override the mj-text block's
    // Typography via CSS specificity, so changing color/size from the
    // sidebar when the whole mj-text is selected looks like a no-op on
    // these templates — reported against the "What Happens Next?" box
    // which is a numbered-list card built from preserved inline HTML.
    //
    // STRATEGY: same as the bg-color propagation at runBgCheck — after a
    // styleUpdate on mj-text, walk the component's content (contentAttr
    // preferred, else view innerHTML) and regex-rewrite any descendant
    // inline declaration of the same typography property to the block's
    // new value. Idempotent: re-running with the same value is a string
    // no-op and no write happens (guard: `updated === source`).
    //
    // SCOPE: only runs for block-level changes. If the sidebar→selection
    // router above handled a word-level wrap, it calls removeStyle on
    // the mj-text so getStyle() returns empty for that prop here → no-op.
    //
    // LOOP SAFETY: the set('content', ...) write is inside the
    // `applyingInline` flag so any nested styleUpdate skips re-entry.
    const TYPO_PROPS_TO_PROPAGATE = [
      'color',
      'font-size',
      'font-family',
      'font-weight',
      'font-style',
      'line-height',
      'letter-spacing',
      'text-decoration',
      'text-transform',
    ] as const;
    let typoDebounceTimer: ReturnType<typeof setTimeout> | null = null;
    // Per-component style snapshot so the styleUpdate handler can diff and
    // propagate only the property the admin actually changed.
    const typoBlockLastStyle = new WeakMap<object, Record<string, string>>();
    // Seed baseline snapshot when mj-text is selected.
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    editor.on('component:selected', (comp: any) => {
      if (!comp || typeof comp.get !== 'function') return;
      if ((comp.get('type') || '').toString() !== 'mj-text') return;
      const s = (comp.getStyle?.() || {}) as Record<string, string>;
      const snap: Record<string, string> = {};
      for (const k of TYPO_PROPS_TO_PROPAGATE) {
        const v = (s[k] || '').toString().trim();
        if (v) snap[k] = v;
      }
      typoBlockLastStyle.set(comp, snap);
    });

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const runTypoPropagate = (mjText: any, changedProps: Record<string, string>) => {
      try {
        const blockValues = changedProps;
        if (!Object.keys(blockValues).length) return;

        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const view = (mjText as any).view;
        const el = view?.el as HTMLElement | undefined;
        const contentAttr: string = (mjText.get?.('content') || '') as string;
        const innerHtml: string = el?.innerHTML || '';
        const source = contentAttr || innerHtml;
        if (!source) return;

        let updated = source;
        for (const [prop, value] of Object.entries(blockValues)) {
          // Negative lookbehind anchors the prop to a property-start
          // boundary so `color` does NOT match inside `background-color` /
          // `border-color` / `outline-color` (earlier bug: picking font
          // color red repainted the whole box's background because the
          // regex rewrote `background-color: #fff` → `background-color:
          // red`). Same precaution blocks `text-decoration` from biting
          // into `text-decoration-color` / `-line` / `-style`.
          // ES2018 lookbehind — supported in all modern browsers.
          // `[^;"']+` match body mirrors the bg-color propagator so
          // escaped quotes in inline styles don't over-match.
          const re = new RegExp(`(?<![-a-zA-Z])${prop}\\s*:\\s*[^;"']+`, 'gi');
          if (re.test(updated)) {
            updated = updated.replace(re, `${prop}: ${value}`);
          }
        }
        if (updated === source) return;

        applyingInline = true;
        try {
          if (contentAttr) {
            mjText.set('content', updated);
          } else {
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            (mjText as any).components?.(updated);
          }
        } finally {
          applyingInline = false;
        }
      } catch (err) {
        console.warn('[TYPO-PROP] propagation failed:', err);
      }
    };

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    editor.on('component:styleUpdate', (comp: any) => {
      if (applyingInline) return;
      if (!comp || typeof comp.get !== 'function') return;
      const type = (comp.get('type') || '').toString();
      if (type !== 'mj-text') return;

      // Delta-detect: only propagate the property the admin actually changed,
      // not all current props. Without this, changing `color` would also push
      // the block's `font-size` into every inner span, overwriting per-span sizes.
      const currentStyle = (comp.getStyle?.() || {}) as Record<string, string>;
      const prevSnap = typoBlockLastStyle.get(comp) as Record<string, string> | undefined;
      const changedProps: Record<string, string> = {};
      if (prevSnap) {
        for (const k of TYPO_PROPS_TO_PROPAGATE) {
          const curr = (currentStyle[k] || '').toString().trim();
          const prev = (prevSnap[k] || '').toString().trim();
          if (curr && curr !== prev) changedProps[k] = curr;
        }
      }
      const nextSnap: Record<string, string> = {};
      for (const k of TYPO_PROPS_TO_PROPAGATE) {
        const v = (currentStyle[k] || '').toString().trim();
        if (v) nextSnap[k] = v;
      }
      typoBlockLastStyle.set(comp, nextSnap);

      if (!Object.keys(changedProps).length) return;
      if (typoDebounceTimer) clearTimeout(typoDebounceTimer);
      typoDebounceTimer = setTimeout(() => {
        typoDebounceTimer = null;
        runTypoPropagate(comp, changedProps);
      }, 30);
    });


    // ── Sync text-align CSS → MJML align attribute ──
    // GrapesJS Style Manager sets text-align as a CSS class rule, but MJML
    // ignores CSS classes — it uses the `align` HTML attribute on mj-* tags.

    editor.on('style:property:update', (prop: any) => {
      try {
        const propName = prop?.get?.('property');
        // Handle both `text-align` (CSS) and `align` (MJML plugin duplicate)
        if (propName !== 'text-align' && propName !== 'align') return;
        const selected = editor.getSelected();
        if (!selected) return;
        const type = (selected.get('type') || '') as string;
        // MJML components that support the align attribute
        const alignable = ['mj-text', 'mj-button', 'mj-image', 'mj-section', 'mj-divider'];
        if (!alignable.includes(type)) return;
        const value = prop.getFullValue?.() || '';
        if (value) {
          selected.addAttributes({ align: value });
        } else {
          selected.removeAttributes('align');
        }
      } catch (err) {
        console.warn('[GrapesEmailEditor] text-align → MJML align sync failed:', err);
      }
    });

    // ── Propagate background-color from <table>/<tr> to descendant cells ──

    //
    // Same root-cause chain as the unified bg handler: `style:property:update`
    // delivers an event arg whose `.get('property')` returns undefined for
    // color properties, so we can't filter by property name. We use
    // `component:styleUpdate` which fires AFTER the write, and read
    // component.style directly. Debounce per-component lastBg state prevents
    // re-firing on unrelated padding/border changes.
    const lastTableBg = new WeakMap<object, string>();
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    editor.on('component:selected', (comp: any) => {
      if (!comp || typeof comp.get !== 'function') return;
      const tagName = (comp.get('tagName') || '').toString().toLowerCase();
      if (!['table', 'tr', 'tbody', 'thead'].includes(tagName)) return;
      const bg = ((comp.getStyle?.() || {})['background-color'] || '') as string;
      lastTableBg.set(comp, bg);
    });
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    editor.on('component:styleUpdate', (comp: any) => {
      try {
        if (!comp || typeof comp.get !== 'function') return;
        const tagName = (comp.get('tagName') || '').toString().toLowerCase();
        if (!['table', 'tr', 'tbody', 'thead'].includes(tagName)) return;
        const style = comp.getStyle?.() || {};
        const currentBg = (style['background-color'] || '') as string;
        const previousBg = lastTableBg.get(comp);
        if (previousBg === undefined) {
          lastTableBg.set(comp, currentBg);
          return;
        }
        if (previousBg === currentBg) return;
        lastTableBg.set(comp, currentBg);
        if (!currentBg) return;
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const cells = (comp as any).find?.('td, th') || [];
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        cells.forEach((cell: any) => {
          const current = cell.getStyle?.() || {};
          cell.setStyle({ ...current, 'background-color': currentBg });
        });
      } catch (err) {
        console.warn('[GrapesEmailEditor] Table bg propagation failed:', err);
      }
    });

    // ── Debounced extraction for structural / style changes ──
    // Timer handle lives in debounceTimerRef (declared at the top of the
    // component) so the unmount cleanup effect can cancel a pending callback
    // before it fires on a torn-down editor. See the ref's declaration for
    // the full Sentry context.
    const debouncedExtract = () => {
      if (debounceTimerRef.current) clearTimeout(debounceTimerRef.current);
      debounceTimerRef.current = setTimeout(() => {
        if (!isLoadedRef.current) return;
        // Typing started during the wait (e.g. clicked back in right after a drop):
        // a rebuild now would replace the text box and jump the cursor to the top.
        // rte:disable extracts when editing ends; Save/Preview compile on their own.
        if (rteActive) return;
        // Editor may have been destroyed between the schedule and now — guard
        // UndoManager access even though the unmount effect should have
        // cancelled the timer already. Defense in depth, not replacement.
        //
        // CRITICAL try/finally: if extractAndNotify throws (mjml-code-to-html
        // failure, DOM glitch, etc.) the UndoManager MUST be restarted, else
        // it stays permanently stopped and the user's Undo button does
        // nothing for the rest of the session.  The previous shape (stop →
        // work → start, all in one try) made `start()` unreachable on throw.
        let umStopped = false;
        try {
          try { editor.UndoManager?.stop?.(); umStopped = true; } catch { /* UM unavailable */ }
          try { extractAndNotify(editor); }
          finally {
            if (umStopped) {
              try { editor.UndoManager?.start?.(); } catch { /* never leave UM stopped */ }
            }
          }
        } catch (err) {
          console.warn('[GrapesEmailEditor] debouncedExtract skipped — editor may be unmounting:', err);
        }
      }, 500);
    };

    // ── Gate ALL extraction behind !rteActive ──
    // During RTE editing, `mjml-code-to-html` triggers the MJML Text
    // component's rerender() which replaces the contenteditable DOM node,
    // destroying cursor focus (GrapesJS/mjml #90).  `component:add` and
    // `component:remove` CAN fire during RTE (e.g., Enter key creates a
    // new component), so they must be gated too — not just component:update.
    // All pending extraction runs on `rte:disable` (see handler above).
    editor.on('component:update', () => {
      if (!rteActive) debouncedExtract();
    });
    editor.on('component:add', () => {
      if (!rteActive) debouncedExtract();
    });
    editor.on('component:remove', () => {
      if (!rteActive) debouncedExtract();
    });
    editor.on('style:property:update', () => {
      if (!rteActive) debouncedExtract();
    });
  }, [extractAndNotify]);

  /**
   * Called when editor is fully ready (canvas mounted, plugins loaded).
   * Safe to load template data here.
   */
  const onReady = useCallback((editor: Editor) => {
    // Load-once guard — prevents double-load if onReady fires more than once
    if (isLoadedRef.current) return;

    cleanupStyleManager(editor);
    keepOnlyDesktopDevice(editor);
    cleanupToolbar(editor);
    overrideFullscreenForPopups(editor);
    injectCanvasDropZoneCSS(editor);
    enableTextAutoDrill(editor);
    keepPenColor(editor);
    cloneColumnAsSection(editor);
    cleanupBlocks(editor);

    const initialValue = initialValueRef.current;
    const initialJson = initialJsonRef.current;

    let loaded = false;

    //  Saved GrapesJS project JSON (perfect re-edit)
    if (!loaded && initialJson && Object.keys(initialJson).length > 0) {
      try {
        editor.loadProjectData(initialJson);
        loaded = true;
      } catch (err) {
        console.warn('[GrapesEmailEditor] Failed to load project JSON, falling back:', err);
      }
    }

    //  Template content (MJML or HTML)
    if (!loaded && initialValue && initialValue.trim()) {
      try {
        // Strip leaked editor marks so already-saved templates open clean.
        const cleanValue = cleanEditorArtifacts(initialValue);
        const isMjml = cleanValue.includes('<mjml') || cleanValue.includes('<mj-');
        const mjml = isMjml ? cleanValue : htmlToMjml(cleanValue);
        editor.setComponents(mjml);
        loaded = true;
      } catch (err) {
        console.warn('[GrapesEmailEditor] Failed to import template, using default:', err);
      }
    }

    // Priority 3: Fresh start with default template
    if (!loaded) {
      editor.setComponents(DEFAULT_MJML_TEMPLATE);
    }

    // Gate all mj-text descendants (tables, divs, h3, spans, etc.) BEFORE
    // marking isLoaded — otherwise the user's first click could hit a
    // still-selectable inner component and block RTE activation.
    markMjTextDescendants(editor);

    // Widen stylable on every component ONCE, right after the tree lands.
    // This is the non-flaky counterpart to the per-click expand handler —
    // covers every component that exists now; the component:add listener
    // below covers anything the admin drops in later.
    expandStylableAcrossTree(editor);

    isLoadedRef.current = true;

    // ── Install snapshot-based Undo/Redo ────────────────────────────────
    // GrapesJS UM v0.22.14 doesn't track changes for this setup (verified
    // via diagnostic logs — internal `track` flag stays false, Undo button
    // is a no-op).  We replace it with a snapshot system: capture
    // editor.getProjectData() after every edit, restore via
    // editor.loadProjectData() on undo/redo.  Same pattern as
    // CertificateTemplateBuilder uses successfully.
    //
    // We override the `core:undo` and `core:redo` GrapesJS commands so the
    // existing toolbar Undo/Redo buttons keep working — no UI change.
    try {
      // Capture the post-load state as the BASELINE — first Undo will
      // bring the user back to this clean loaded state.
      currentStateRef.current = editor.getProjectData();

      const captureSnapshot = () => {
        if (isApplyingHistoryRef.current) return;       // skip during undo/redo
        if (!isLoadedRef.current) return;
        // Editor-readiness gate: getProjectData() iterates Pages, Styles and
        // Components collections internally; if ANY of those collections has
        // been swapped to undefined (transient state during template load,
        // grapesjs-mjml recompile, or unmount tear-down) the call throws
        // "Cannot read properties of undefined (reading 'forEach')".  Bail
        // silently in that window — the next user edit re-fires the debounced
        // snapshot and captures a healthy state.
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const ed = editor as any;
        if (!ed?.Pages?.getAll || !ed?.getProjectData) return;
        try {
          const snap = editor.getProjectData();
          // Skip if state hasn't changed (rapid identical events).
          if (currentStateRef.current && JSON.stringify(snap) === JSON.stringify(currentStateRef.current)) {
            return;
          }
          // Push the OLD current state to the undo stack, then update current.
          if (currentStateRef.current) {
            undoStackRef.current.push(currentStateRef.current);
            if (undoStackRef.current.length > 50) {
              undoStackRef.current.shift(); // bound memory
            }
          }
          currentStateRef.current = snap;
          // A new edit invalidates any pending redos.
          redoStackRef.current = [];
        } catch (err) {
          // Demoted from warn → debug: GrapesJS occasionally throws here when
          // an internal collection is mid-swap (e.g. during MJML recompile).
          // The next debounce tick captures the post-swap state, so missing
          // one snapshot is harmless.  Keep the log so dev tools still surface
          // it under verbose, but no longer pollute production console.
          if (typeof console.debug === 'function') {
            console.debug('[GrapesEmailEditor] snapshot capture skipped (editor mid-update):', err);
          }
        }
      };

      const scheduleSnapshot = () => {
        if (historyDebounceRef.current) clearTimeout(historyDebounceRef.current);
        historyDebounceRef.current = setTimeout(() => {
          historyDebounceRef.current = null;
          captureSnapshot();
        }, 200); // 200ms feels instant while still coalescing color-drag bursts
      };

      // Flush any pending snapshot synchronously — used right before Undo
      // so the user's most-recent edit IS in the stack to revert from.
      const flushPendingSnapshot = () => {
        if (historyDebounceRef.current) {
          clearTimeout(historyDebounceRef.current);
          historyDebounceRef.current = null;
          captureSnapshot();
        }
      };

      // Hook all the events that mean "user changed something".
      // Gate behind !rteActive: during text editing the mj-text model's `content`
      // hasn't been synced back yet (syncs only on rte:disable), so snapshots
      // would be stale.  More importantly, getProjectData() during RTE can trigger
      // grapesjs-mjml side effects that replace the contenteditable DOM and lose
      // the cursor.  Snapshot is captured on rte:disable (content is stable then).
      editor.on('component:add',          () => { if (!rteActiveRef.current) scheduleSnapshot(); });
      editor.on('component:remove',       () => { if (!rteActiveRef.current) scheduleSnapshot(); });
      editor.on('component:update',       () => { if (!rteActiveRef.current) scheduleSnapshot(); });
      editor.on('style:property:update',  () => { if (!rteActiveRef.current) scheduleSnapshot(); });

      // Override core:undo — runs when user clicks the toolbar Undo button.
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      (editor as any).Commands.add('core:undo', {
        run: (ed: Editor) => {
          // Flush any pending debounced snapshot FIRST so the user's most
          // recent edit is captured in the stack before we pop.  Otherwise
          // a click-Undo within 200ms of an edit reverts to the wrong state.
          flushPendingSnapshot();
          if (undoStackRef.current.length === 0) return;
          const previous = undoStackRef.current.pop();
          if (currentStateRef.current) {
            redoStackRef.current.push(currentStateRef.current);
          }
          currentStateRef.current = previous;
          isApplyingHistoryRef.current = true;
          try {
            ed.loadProjectData(previous);
          } catch (err) {
            console.warn('[GrapesEmailEditor] Undo restore failed:', err);
          } finally {
            // loadProjectData fires many events synchronously and asynchronously;
            // 400ms covers the burst, after which captures resume normally.
            setTimeout(() => { isApplyingHistoryRef.current = false; }, 400);
          }
        },
      });

      // Override core:redo — symmetric to undo.
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      (editor as any).Commands.add('core:redo', {
        run: (ed: Editor) => {
          flushPendingSnapshot();
          if (redoStackRef.current.length === 0) return;
          const next = redoStackRef.current.pop();
          if (currentStateRef.current) {
            undoStackRef.current.push(currentStateRef.current);
          }
          currentStateRef.current = next;
          isApplyingHistoryRef.current = true;
          try {
            ed.loadProjectData(next);
          } catch (err) {
            console.warn('[GrapesEmailEditor] Redo restore failed:', err);
          } finally {
            setTimeout(() => { isApplyingHistoryRef.current = false; }, 400);
          }
        },
      });
    } catch (err) {
      console.warn('[GrapesEmailEditor] Snapshot undo install failed:', err);
    }

    // Sync editor_json ONLY on initial load — do NOT overwrite html_template.
    // Rationale: the DB already has the admin's real HTML.  Calling the full
    // extractAndNotify here would compile the just-loaded MJML back to HTML
    // (possibly slightly different due to roundtrip) and push it into the
    // parent's formData.  A subsequent Save would then overwrite the original
    // DB template with this (potentially empty or degraded) compiled version.
    // Observed bug: saved html_template ended up as just empty wrapper tables.
    // User edits trigger the full extractAndNotify via component:update event.
    onJsonChangeRef.current?.(editor.getProjectData());

    // Register variable blocks NOW — the useEffect([variables]) may have already
    // fired and bailed because isLoadedRef was false at that point.
    if (variablesRef.current?.length) {
      registerVariableBlocks(editor, variablesRef.current);
    }
  }, []);

  /**
   * Register variable blocks whenever the variables prop changes.
   */
  useEffect(() => {
    const editor = editorRef.current;
    if (!editor || !variables || !isLoadedRef.current) return;
    registerVariableBlocks(editor, variables);
  }, [variables]);

  // Memoize GrapesJS options — stable reference prevents GjsEditor re-init
  // when parent re-renders (e.g., after onChange updates formData).
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const editorOptions = useMemo<any>(() => ({
    height: '100%',
    fromElement: false,
    storageManager: false,
    undoManager: { trackSelection: false },
    canvas: {
      styles: [
        'https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700&display=swap',
      ],
    },
    assetManager: {
      upload: false,
      autoAdd: true,
      uploadFile: async (ev: Event) => {
        const files: File[] = [];
        const dt = (ev as DragEvent).dataTransfer;
        const target = ev.target as HTMLInputElement | null;
        if (dt?.files) Array.from(dt.files).forEach((f) => files.push(f));
        else if (target?.files) Array.from(target.files).forEach((f) => files.push(f));
        if (!files.length) return;
        try {
          const urls: string[] = [];
          for (const file of files) {
            const resp = await storageService.uploadFile(file);
            if (resp?.url) urls.push(resp.url);
          }
          if (urls.length) {
            editorRef.current?.AssetManager.add(
              urls.map((src) => ({ src, type: 'image' })),
            );
            toast.success(
              `${urls.length} image${urls.length > 1 ? 's' : ''} uploaded — click thumbnail to insert`,
            );
          }
        } catch (err) {
          console.error('[GrapesEmailEditor] Image upload failed:', err);
          toast.error('Image upload failed');
        }
      },
    },
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }), []);

  const isPercentHeight = minHeight.trim().endsWith('%');

  return (
    <div
      className={isPercentHeight ? 'flex flex-col h-full' : undefined}
    >
      {/* CSS fallback — hide device-switcher toolbar if any button survives
          the programmatic prune in keepOnlyDesktopDevice.  Desktop-only mode
          means the whole devices panel becomes redundant. */}
      <style>{`
        .gjs-pn-devices-c .gjs-pn-btn:not(.gjs-pn-active) { display: none !important; }
        .gjs-pn-devices-c .gjs-pn-btn.gjs-pn-active { display: none !important; }

        /* ── RTE toolbar fixes ──
           DOM: .gjs-rte-toolbar.gjs-one-bg > .gjs-rte-actionbar > span.gjs-rte-action
           Default CSS: .gjs-rte-action { width:25px; padding:5px; border-right:1px solid } */
        .gjs-rte-toolbar {
          background-color: #1f2937 !important;
          border: none !important;
          border-radius: 6px !important;
          box-shadow: 0 4px 12px rgba(0,0,0,0.25) !important;
          padding: 4px 6px !important;
        }
        .gjs-rte-actionbar {
          display: flex !important;
          align-items: center !important;
          gap: 2px !important;
        }
        .gjs-rte-action {
          width: auto !important;
          min-width: 28px !important;
          height: 28px !important;
          padding: 0 5px !important;
          border-right: none !important;
          border-radius: 4px !important;
          font-family: Arial, sans-serif !important;
          font-size: 14px !important;
          color: #d1d5db !important;
        }
        .gjs-rte-action:hover {
          background-color: rgba(255,255,255,0.1) !important;
        }
        .gjs-rte-active {
          background-color: rgba(255,255,255,0.18) !important;
          color: #fff !important;
        }
        /* Select wrapper — let the select dictate its own width */
        .gjs-rte-action:has(.gjs-rte-var-select) {
          min-width: unset !important;
          padding: 0 2px !important;
          background: transparent !important;
        }
        /* Insert Variable dropdown */
        .gjs-rte-var-select {
          height: 26px !important;
          color: #d1d5db !important;
          background-color: rgba(255,255,255,0.08) !important;
          border: 1px solid rgba(255,255,255,0.18) !important;
          border-radius: 4px !important;
          padding: 0 4px !important;
          font-size: 12px !important;
          font-family: Arial, sans-serif !important;
          cursor: pointer !important;
        }
        .gjs-rte-var-select:hover {
          background-color: rgba(255,255,255,0.16) !important;
        }
        .gjs-rte-var-select option {
          background: #1f2937 !important;
          color: #e5e7eb !important;
        }

        /* ── CSS-class-based fullscreen ──
           We override GrapesJS's default fullscreen command (which uses the
           native Fullscreen API) because native fullscreen hides any popup
           attached to document.body — Style Manager's color picker, our link
           popup, dropdowns — behind the fullscreen layer.  By using a CSS
           class instead, the body remains the parent and popups stay
           visible/interactive in the maximised view. */
        .gjs-email-editor-fullscreen {
          position: fixed !important;
          inset: 0 !important;
          width: 100vw !important;
          height: 100vh !important;
          z-index: 9998 !important;
          background: #fff !important;
        }
        body.gjs-email-editor-fullscreen-active {
          overflow: hidden !important;
        }
      `}</style>
      {label && (
        <label className="block text-sm font-medium text-gray-700 mb-1 flex-shrink-0">
          {label} {required && <span className="text-red-500">*</span>}
        </label>
      )}

      <div
        className={`rounded-lg overflow-hidden ${isPercentHeight ? 'flex-1 min-h-0' : ''} ${
          error ? 'border-2 border-red-500' : 'border border-gray-300'
        }`}
        style={isPercentHeight ? undefined : { height: minHeight }}
      >
        <GjsEditor
          className="h-full"
          grapesjs={grapesjs}
          plugins={PLUGINS}
          onEditor={onEditor}
          onReady={onReady}
          options={editorOptions}
        />
      </div>

      {error && <p className="text-sm text-red-600 mt-1">{error}</p>}
    </div>
  );
}
